<template>
    <div id="prescription-verification-container">
        <card v-if="!isLoading">
            <EntityAlertsComponent ref="entity-alerts" v-if="(prescription && prescription.rxNumber) || pageMode == 'pv2'"
                :prescription="prescription"
                :prescriber="doctor"
                :drug="drug"
                :drug-source="drug?.source"
                :store-id="prescription?.storeID"
                :fill-date="prescription?.fillDate"
                :rx-status="prescription?.status"
                :expiration-date="prescription?.expirationDate"
                class="pb-2">
                <template #default>
                    <h1 class="m-0">{{ pageTitle }}</h1>
                </template>
                <template v-if="pageMode != 'audit'" #accessory>
                    <div class="d-flex align-items-center mr-4" style="gap: 5px;">
                        <!-- Batch Navigation Controls -->
                        <div v-if="hasBatchPrescriptions()" class="d-flex align-items-center"
                            style="gap: 10px; border-right: 2px solid #dee2e6; padding-right: 15px; margin-right: 15px;">
                            <span class="badge badge-info" style="padding: 8px 12px; font-size: 0.875rem;">
                                <i class="fas fa-layer-group"></i> {{ remainingBatchCount() + 1 }} Scripts in Batch
                            </span>
                            <b-button variant="outline-danger" @click="navigateToNextInBatch" size="sm">
                                Skip <i class="fas fa-forward"></i>
                            </b-button>
                        </div>
                        <b-button v-if="prescription && prescription.rxNumber" size="sm"
                            :variant="prescription.isUrgent ? 'warning' : 'outline-warning'"
                            @click="toggleUrgent">
                            <i class="fas fa-exclamation-triangle"></i>
                            {{ prescription.isUrgent ? 'Urgent' : 'Mark Urgent' }}
                        </b-button>
                        <b-button v-if="prescription && prescription.rxNumber && prescription.pV2ID" variant="danger"
                            @click="cancelPV2" size="sm">
                            <strong>
                                Cancel PV2...
                            </strong>
                        </b-button>
                        <!-- Wrapped so the tooltip still fires: a disabled button emits no mouse events. -->
                        <span v-if="showAcceptAndTransferButton" v-b-tooltip.hover
                            :title="acceptAndTransferDisabledReason">
                            <b-button variant="warning" v-b-modal.accept-and-transfer-modal size="sm"
                                :disabled="disableAcceptAndTransferButton">
                                <strong>
                                    <i class="fas fa-exchange-alt"></i> Accept & Transfer
                                </strong>
                            </b-button>
                        </span>
                        <b-button variant="primary" href="#" @click.prevent="printByRxRf()" size="sm"
                            v-b-tooltip.hover.ds750
                            title="Reprints the patient education monograph (drug counseling pages) for this prescription. Does not reprint the bottle Rx label.">
                            Print by Rx/Rf <i class="fas fa-print"></i>
                        </b-button>
                        <PatientEducationModalComponent v-if="prescription && prescription.packageID"
                            :drug-id="prescription.packageID" :drug-source="prescription.drugSource" />
                        <b-button target="_blank" variant="primary"
                            :to="{ name: 'PatientDashboard', params: { id: patient.id } }" size="sm">
                            <span>Go To Profile</span> <i class="fas fa-user"></i>
                        </b-button>
                        <b-button variant="primary" @click="openCreateTaskModal" size="sm"
                            :disabled="!patient.id">
                            <span>New Task</span> <i class="fas fa-check-circle"></i>
                        </b-button>
                        <b-button v-if="pageMode === 'pv1' && canCreatePA" id="pa-create-button" variant="primary"
                            size="sm" :disabled="!patient.id" @click="newPriorAuthorization">
                            <span>Create PA</span> <i class="fas fa-file-medical"></i>
                        </b-button>
                        <CreateConsultationButton v-if="pageMode === 'pv1'" :patient="patient" :store="store"
                            :prescription="prescription" variant="primary" size="sm" :show-icon="true" />
                        <b-dropdown variant="primary" right size="sm">
                            <template #button-content>
                                Contact<template v-if="pageMode == 'pv2'"><br></template> Prescriber
                            </template>
                            <b-dropdown-item v-b-modal="'pv-request-rx-clarification-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )">
                                RX Clarification
                            </b-dropdown-item>
                            <b-dropdown-item v-b-modal="'pv-request-alternate-rx-fax'" :disabled="!(
                                prescription &&
                                prescription.rxNumber &&
                                prescription.rfNumber != null
                            )">
                                Request Alternate Medication
                            </b-dropdown-item>
                            <b-dropdown-divider v-if="filteredCustomContactPrescriptionTemplates.length" />
                            <b-dropdown-item v-for="template in filteredCustomContactPrescriptionTemplates"
                                :key="template.id"
                                @click="customContactPrescriberTemplate = template; $bvModal.show('pv-custom-prescriber-fax')"
                                :disabled="!(
                                    prescription &&
                                    prescription.rxNumber &&
                                    prescription.rfNumber != null
                                )">
                                {{ template.name }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </template>
            </EntityAlertsComponent>
            <template v-else>
                <h1 class="mb-3">
                    {{ pageTitle }}
                </h1>
                <b-form-row id="prescription-search-row">
                    <b-col>
                        <SearchComponent v-model="prescription" label="Prescription" search-u-r-l="/Prescription"
                            @input="setIsLoading" />
                    </b-col>
                </b-form-row>
            </template>

            <b-form-row id="alert-row">
                <b-col>
                    <DispensingErrorAlertComponent v-for="(err, index) in dispenseErrors" :key="err.id"
                        :confirmationModalId="'errorConfirmationModal_' + index"
                        :buttonTitles="dispenseErrorAlertButtonTitles" v-model="dispenseErrors[index]"
                        @onStatusChange="handleErrorStatusChange" ref="DispensingErrorAlertComponents" />
                </b-col>
            </b-form-row>

            <b-form-row v-show="!!(prescription && prescription.rxNumber)" id="prescription-verification-row"
                name="prescription-verification-row">

                <b-col id="image-column" cols="5" v-show="pageMode != 'audit'">
                    <!-- IMAGE -->
                    <div class="image-wrapper">
                        <b-button v-if="prescription.escriptID" variant="warning" v-b-modal.all-escripts>
                            <strong>All Escript Images...</strong>
                            <escript-list-modal v-model="prescription.rxID" id="all-escripts" />
                        </b-button>
                        <ImageComponent :imageID="prescription.imageID" :escriptID="prescription.escriptID"
                            :rxNumber="prescription.rxNumber" ref="prescriptionImage" class="script-image" />
                        <FilledImagesComponent :rx-id="prescription.rxID" :modal-presentation="false" :hide-title="true"
                            class="filled-image" />

                        <HardCopyPrescriptionsComponent
                            :imageID="prescription.imageID"
                            :currentStoreID="prescription.storeID"
                            :currentRxNumber="prescription.rxNumber"
                            :currentRfNumber="prescription.rfNumber"
                            :currentPatientID="prescription.patientID"
                            :showCrossPatient="showCrossPatientHardCopyScripts"
                            :hideSamePatient="hideSamePatientHardCopyScripts"
                            @count-changed="siblingScriptCount = $event"
                        />

                        <!-- Typing/Verification Info -->
                        <div class="typing-info mt-2">
                            <div v-if="prescription.createdBy">
                                <b>Typed By</b>
                                <span class="name">{{ prescription.createdBy?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.created) }}</span>
                            </div>
                            <div v-if="prescription.pV1ID">
                                <b>PV1 By</b>
                                <span class="name">{{ prescription.pv1By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv1) }}</span>
                            </div>
                            <div v-if="prescription.pV2ID">
                                <b>Verified By</b>
                                <span class="name">{{ prescription.pv2By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv2) }}</span>
                            </div>
                        </div>
                    </div>
                </b-col>

                <!-- DETAILS COLUMNS-->
                <b-col id="details-column">
                    <b-form-row id="prescription-identifier-row">
                        <b-col>
                            <strong>Rx #{{ prescriptionIdentifier }}</strong>
                            <PrescriptionStatusBadge v-if="pageMode === 'pv1'" :prescription="prescription"
                                class="ml-2" />
                        </b-col>
                    </b-form-row>

                    <b-form-row v-if="pageMode === 'pv2' && prescription.pV1ID" id="pv1-info-row" class="bordered">
                        <b-col class="typing-info">
                            <div>
                                <b>PV1 By</b>
                                <span class="name">{{ prescription.pv1By?.split('@')[0] }}</span>
                                <span class="date">{{ formattedDateAndTime(prescription.pv1) }}</span>
                            </div>
                        </b-col>
                    </b-form-row>

                    <div class="highlight p-3 mb-2" v-if="valueChangesSinceBaseline && valueChangesSinceBaseline.length">
                        <h6 class="mb-0">
                            <i :class="{ 'fa': true, 'fa-chevron-right': true, 'fa-rotate-90': showAllChanges }"
                                style="transition: all .2s;" />
                            Has Changes Since Baseline Verification
                        </h6>

                        <div v-if="showAllChanges" class="changes-grid mt-3">
                            <div v-for="i in valueChangesSinceBaseline">
                                <strong>{{ camelCaseToProperCase(i.key) + ":" }}</strong><br>

                                <template v-if="i.source && !i.target">
                                    Removed {{ i.source }}
                                </template>
                                <template v-else-if="i.target && !i.source">
                                    Added {{ i.target }}
                                </template>
                                <template v-else>
                                    {{ i.source }}
                                    <i class="fa fa-arrow-right" style="font-size: 1em;"></i>
                                    {{ i.target }}
                                </template>
                            </div>
                        </div>
                    </div>

                    <b-form-row id="patient-row" class="bordered">
                        <b-col>
                            <b-form-row>
                                <b-col v-if="showExplicitVerification" cols="1">
                                    <n-switch id="switch_0" v-model="patientStatus" color="green"
                                        :class="{ 'selected': (verificationIndex === 0) }">
                                        <template #on>
                                            <i class="fa fa-check" />
                                        </template>
                                        <template #off>
                                            <i class="fa fa-times" style="color:red;" />
                                        </template>
                                    </n-switch>
                                </b-col>
                                <b-col>
                                    <b-form-row>
                                        <b-col>
                                            <strong :class="patientNameMatchClass">Patient Name:</strong>
                                            <p>{{ patient.displayNamesForPerson() }}</p>
                                            <p>{{ patient && patientAddressString }}</p>
                                            <p><b>Pn:</b> {{ patient && patient.phoneDisplay }}
                                                <PhoneCopyButton v-if="patient.phoneNumber?.length > 0" :phone="patient.phoneNumber" />
                                            </p>
                                            <p><b>Cn:</b> {{ patient && patient.cellDisplay }}
                                                <PhoneCopyButton v-if="patient.cellNumber?.length > 0" :phone="patient.cellNumber" />
                                            </p>
                                        </b-col>
                                        <b-col>
                                            <strong :class="patientDateOfBirthMatchClass">DOB:</strong>
                                            <p v-if="patient && patient.dateOfBirth && patientIsUnder12"
                                                :style="{ 'font-weight': 'bold', color: 'red' }">
                                                {{ patientDobString }}
                                            </p>
                                            <p v-else>
                                                {{ patientDobString }}
                                            </p>
                                            <p v-if="patient && patient.licenseNumber">License Number: {{
                                                    patient.licenseNumber
                                                }}</p>
                                            <p v-if="patient && patient.ssn">SSN: {{ patient.ssn }}</p>
                                            <p><b>Sex:</b> {{ patient && patient.sex }}</p>
                                        </b-col>
                                    </b-form-row>
                                    <b-form-row>
                                        <b-col>
                                            <strong :class="patientAllergyMatch">Allergies:</strong>
                                            <p style="">
                                                {{ patient && patientAllergyString }}</p>

                                            <strong>Active Diagnosis:</strong>
                                            <div v-if="patient && patientGSDDPatientDiagnosis.length">
                                                <p v-for="code in patientGSDDPatientDiagnosis" :key="code.id">
                                                    {{ code.icD10Code }} - {{ code.icD10Description }}
                                                </p>
                                            </div>
                                            <p v-else>None</p>
                                        </b-col>
                                        <b-col>
                                            <div class="actions my-2">
                                                <b-button @click="historyModal.openRxHistory()"
                                                    variant="outline-primary" size="sm"
                                                    :disabled="!(patient && patient.id)" class="m-0">
                                                    Show This History{{ thisHistoryCount > 0 ? ` (${thisHistoryCount})` : '' }}
                                                </b-button>
                                                <b-button @click="rxHistoryModal.openRxHistory()"
                                                    variant="outline-primary" size="sm"
                                                    :disabled="!(patient && patient.id)" class="m-0">Show RX History{{ prescriptionCount > 0 ? ` (${prescriptionCount})` : '' }}
                                                </b-button>
                                                <b-button v-b-modal.other-med-modal variant="outline-primary" size="sm"
                                                    :disabled="!(patient && patient.id)" class="m-0">
                                                    Show Other Meds{{ otherMedsCount > 0 ? ` (${otherMedsCount})` : '' }}
                                                </b-button>
                                                <b-button v-b-modal.patient-notes-modal variant="outline-primary"
                                                    size="sm" :disabled="!(patient && patient.id)" class="m-0">
                                                    Show Patient Notes{{ notesCount > 0 ? ` (${notesCount})` : '' }}
                                                </b-button>
                                                <b-button @click="openRxEdit" variant="outline-primary" size="sm"
                                                    :disabled="!(patient && patient.id)" class="m-0">
                                                    Edit Rx
                                                </b-button>
                                                <b-button v-b-modal.label-reprint-modal variant="outline-primary"
                                                    size="sm" class="m-0"
                                                    v-b-tooltip.hover.ds750
                                                    title="Reprints the patient bottle Rx label for this prescription. Opens a printer selection dialog.">
                                                    Print Label
                                                </b-button>
                                            </div>
                                        </b-col>
                                    </b-form-row>
                                </b-col>
                            </b-form-row>
                        </b-col>
                    </b-form-row>
                    <div>
                        <b-modal id="other-med-modal" title="Other Medications" size="xl">
                            <b-card>
                                <other-medications-component :patient="patient" disabled onlyActive />
                            </b-card>
                            <template #modal-footer>
                                <b-button variant="dark" @click="$bvModal.hide('other-med-modal')">
                                    Close
                                </b-button>
                            </template>
                        </b-modal>
                    </div>
                    <!-- Reprint-Label-Modal-->
                    <div>
                        <b-modal id="label-reprint-modal" title="Reprint Label">
                            <b-row>
                                <b-col>
                                    <b-dropdown :text="activePrinter ? prettyPrinterName(activePrinter) : 'IP Address'"
                                        variant="outline-primary">
                                        <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                            :active="p.name === activePrinter?.name" @click="activePrinter = p">
                                            <i class="fa fa-print"></i>
                                            {{ prettyPrinterName(p) }}
                                            <div class="d-flex" style="gap: 6px;">
                                                <small v-if="p.default">Local Default</small>
                                                <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                            </div>
                                        </b-dropdown-item-button>
                                        <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                            <i class="fa fa-info-circle" />
                                            To print to local printers, install<br>the Scriptly Client Service
                                        </b-dropdown-item>
                                        <b-dropdown-divider />
                                        <b-dropdown-item-button :active="!activePrinter" @click="activePrinter = null">
                                            <i class="fa fa-globe"></i>
                                            IP Address...
                                        </b-dropdown-item-button>
                                    </b-dropdown>
                                    <fg-input v-if="showIpInput" type="text" placeholder="Enter IP Address Here"
                                        v-model="printerIpAddress" />
                                </b-col>
                            </b-row>
                            <template #modal-footer>
                                <b-overlay :show="isReprintingLabel" spinner-small>
                                    <b-button @click="getPrinterByIPAddress" variant="success"
                                        v-b-tooltip.hover.ds750
                                        title="Sends the patient bottle Rx label to the selected printer (or the entered IP address).">
                                        Print
                                    </b-button>
                                </b-overlay>
                            </template>
                        </b-modal>
                    </div>

                    <b-form-row id="doctor-row" class="bordered">
                        <b-col v-if="showExplicitVerification" cols="1">
                            <n-switch id="switch_1" v-model="doctorStatus" color="green"
                                :class="{ 'selected': (verificationIndex === 1) }">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                        </b-col>
                        <b-col>
                            <strong :class="doctorNameMatch">Doctor Name:</strong>
                            <p>{{ doctorNameLabel }}</p>
                            <p>{{ doctor && doctorAddressString }}</p>
                        </b-col>
                        <b-col>
                            <strong :class="doctorNpiMatch">NPI:</strong>
                            <p>{{ doctor && doctor.npiNumber }}
                                <NpiValidationBadge v-if="doctor?.id" :prescriber-id="doctor.id" />
                            </p>
                        </b-col>
                        <b-col>
                            <strong :class="doctorDeaMatch">DEA:</strong>
                            <p>{{ doctor && doctor.deaNumber }}</p>
                        </b-col>
                        <b-col>
                            <strong>Phone:</strong>
                            <p>{{ doctor && doctor.phoneNumber | formatPhone }}
                                <PhoneCopyButton v-if="doctor.phoneNumber?.length > 0" :phone="doctor.phoneNumber" />
                            </p>
                            <strong>Fax:</strong>
                            <p>{{ doctor && doctor.faxNumber | formatPhone }}
                                <a v-if="doctor.faxNumber?.length > 0" href="#" class="mx-1" v-b-tooltip.hover title="Copy">
                                    <i class="fas fa-copy" @click.prevent="copy(doctor.faxNumber)" />
                                </a>
                            </p>
                        </b-col>
                        <b-col v-if="showSupervisingPhysician">
                            <strong>Supervising Physician:</strong>
                            <p>{{ supervisingPhysicianDisplay }}</p>
                        </b-col>
                    </b-form-row>

                    <!-- PMP Gateway Widget — visible only when configured and user has Pharmacists/PMP Gateway permission -->
                    <PmpGatewayWidget v-if="patient && patient.id" :patient="patient" :store-id="prescription && prescription.storeID" class="mx-1" />

                    <b-form-row id="drug-row" class="bordered">
                        <b-col v-if="showExplicitVerification" cols="1">
                            <n-switch id="switch_2" v-model="drugStatus" color="green"
                                :class="{ 'selected': (verificationIndex === 2) }">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                        </b-col>
                        <b-col>
                            <b-form-row class="pb-2">
                                <b-col>
                                    <strong :class="drugMatch">Drug:{{drugMatch != 'text-success' && dawDrugMismatch ? ' (DAW NDC Mismatch)' : '' }}</strong><br />
                                    <h5>
                                        {{ drug?.productNameShort }}
                                        <b-badge v-if="prescription.needsRefrigeration" variant="info" class="ml-1"
                                            v-b-tooltip.hover title="Refrigerated item. Package with cold-chain handling.">
                                            <i class="fas fa-snowflake"></i> REFRIGERATED
                                        </b-badge>
                                    </h5>
                                    <small v-if="drug?.overrideName" class="text-info">Override: {{ drug.overrideName }}</small>
                                </b-col>
                                <b-col align-self="end" cols="3">
                                    <b-button v-b-modal.drug-change-modal size="sm" variant="success">Edit</b-button>
                                    <div v-if="drugDetails != null">
                                        <DrugChangeModal v-model="drugModalVisible" :prescription="prescription"
                                            :drug-details="drugDetails" @change-drug="drugChangeComplete" />
                                    </div>
                                </b-col>
                            </b-form-row>
                            <b-form-row>
                                <b-col>
                                    <AllergyWarningComponent :patient-id="patient && +patient.id" :drug="drugDetails" />
                                </b-col>
                            </b-form-row>
                            <b-form-row
                                :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'PackageID') }">
                                <b-col cols="4">
                                    <b-img v-for="drugImage of drugImageURLs" v-show="drugImageURLs.length"
                                        :key="drugImage" :src="drugImage" onerror="this.style.display='none'" fluid />
                                </b-col>
                                <b-col>
                                    <b-form-row>
                                        <b-col>
                                            <strong :class="drugMatch">NDC:</strong>
                                            {{ drugDetails && drugDetails.ndc }} {{
                                                drugMatch != 'text-success' &&
                                                dawDrugMismatch ? '(DAW NDC Mismatch) ' : ''
                                            }}
                                        </b-col>
                                    </b-form-row>
                                    <b-form-row>
                                        <b-col>
                                            <strong>UPC:</strong>
                                            {{ upc }}
                                        </b-col>
                                    </b-form-row>
                                    <b-form-row v-if="drugDetails && drugDetails?.packageDescription">
                                        <b-col>
                                            <strong>Package Description:</strong><br>
                                            {{ drugDetails.packageDescription }}
                                        </b-col>
                                    </b-form-row>
                                    <b-form-row>
                                        <b-col>
                                            <strong>Package Size:</strong>
                                            {{
                                                drugDetails && `${drugDetails.packageSize} ${drugDetails.billingUnit}`
                                            }}
                                        </b-col>
                                    </b-form-row>
                                    <b-form-row>
                                        <b-col>
                                            <strong>On-Hand:</strong>
                                            {{ inventoryOnHand }} {{ drugDetails?.billingUnit || 'units' }}
                                        </b-col>
                                        <b-col>
                                            <strong>Allocated:</strong>
                                            {{ inventoryAllocated }} {{ drugDetails?.billingUnit || 'units' }}
                                        </b-col>
                                        <b-col>
                                            <strong>Available:</strong>
                                            <span :class="{
                                                'text-danger': (inventoryOnHand - inventoryAllocated) < (prescription?.quantity || 0),
                                                'text-success': (inventoryOnHand - inventoryAllocated) >= (prescription?.quantity || 0)
                                            }">
                                                {{ inventoryOnHand - inventoryAllocated }} {{ drugDetails?.billingUnit || 'units' }}
                                            </span>
                                        </b-col>
                                    </b-form-row>
                                </b-col>
                            </b-form-row>
                            <!-- Compound ingredients live inside the drug section (compounds only). -->
                            <b-form-row
                                v-if="hasCompoundReadPermission && prescription && prescription.drugSource === 2 && drugDetails && drugDetails.ingredients && drugDetails.ingredients.length"
                                id="compound-ingredients-row">
                                <b-col>
                                    <CompoundIngredientsDisplay
                                        :ingredients="drugDetails.ingredients"
                                        :drug-id="drugDetails.drugId"
                                        :store-id="prescription.storeID"
                                        :rx-number="prescription.rxNumber"
                                        :rf-number="prescription.rfNumber"
                                        :compound-preset-id="prescription.compoundPresetID"
                                        :is-billed="!isCompletelyUnbilled"
                                        :collapsible="true" />
                                </b-col>
                            </b-form-row>
                        </b-col>
                    </b-form-row>

                    <b-form-row id="quantity-row" class="bordered">
                        <b-col v-if="showExplicitVerification" cols="1">
                            <n-switch id="switch_3" v-model="quantityStatus" color="green"
                                :class="{ 'selected': (verificationIndex === 3) }">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                        </b-col>
                        <b-col>
                            <b-form-row>
                                <b-col>
                                    <b-form-row>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'Quantity') }">
                                            <strong :class="quantityMatch">Quantity:</strong>
                                            <p>{{ prescription && prescription.quantity }}</p>
                                        </b-col>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'Dispensed') }">
                                            <strong>Dispensed:</strong>
                                            <p>{{ prescription && prescription.dispensed }}</p>
                                        </b-col>
                                        <b-col></b-col>
                                        <b-col></b-col>
                                    </b-form-row>
                                    <b-form-row>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'Refills') }">
                                            <strong :class="refillsMatch">Refills:</strong>
                                            <p>{{ prescription.refills }}</p>
                                        </b-col>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'TotalDispensed') }">
                                            <strong>Qty Left:</strong>
                                            <p>
                                                {{
                                                    formattedPrecision(prescription.totalAllowedToDispense -
                                                        prescription.totalDispensed, 2)
                                                }}
                                            </p>
                                        </b-col>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'DaySupply') }">
                                            <strong>Days' Supply:</strong>
                                            <p>{{ prescription && prescription.daySupply }}</p>
                                        </b-col>
                                        <b-col
                                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'DeliveryCode') }">
                                            <strong>Delivery:</strong>
                                            <p>{{ deliveryCodeText }}</p>
                                        </b-col>
                                    </b-form-row>
                                </b-col>
                            </b-form-row>
                            <b-form-row>
                                <b-col>
                                    <div :class="safetyCapClasses">
                                        <i :class="safetyCapIcon"></i>
                                        <strong>{{ safety }}</strong>
                                    </div>
                                </b-col>
                            </b-form-row>
                        </b-col>
                    </b-form-row>

                    <b-form-row id="directions-row" class="bordered">

                        <b-col v-if="showExplicitVerification" cols="1">
                            <n-switch id="switch_4" v-model="directionStatus" color="green"
                                :class="{ 'selected': (verificationIndex === 4) }">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                        </b-col>
                        <b-col class="pb-2"
                            :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'Directions') }">
                            <strong>Directions:</strong>
                            <p id="directions">{{ prescription.directions }}</p>
                        </b-col>

                    </b-form-row>

                    <!-- Financial info-->
                    <b-form-row id="codes-row" class="bordered">
                        <b-col :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'OriginCode') }">
                            <strong :class="originOk">Origin:</strong>
                            <p>{{ originCodeText }}</p>
                        </b-col>
                        <b-col :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'DawCode') }">
                            <strong :class="dawMatch">DAW:</strong>
                            <strong v-if="prescription.dawCode != 0"><br /><a>{{ dawCodeText }}</a></strong>
                            <p v-else>{{ dawCodeText }}</p>
                        </b-col>
                    </b-form-row>

                    <b-form-row id="date-rows" class="bordered">
                        <b-col>
                            <b-form-row>
                                <b-col>
                                    <strong :class="fillDateOk">Fill Date:</strong>
                                    <p>{{ fillDate }}</p>
                                </b-col>
                                <b-col
                                    :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'WrittenDate') }">
                                    <strong :class="writtenMatch">Written:</strong>
                                    <p>{{ writtenDate }}</p>
                                </b-col>
                                <b-col
                                    :class="{ highlight: !!valueChangesSinceBaseline?.find(c => c.key === 'ExpirationDate') }">
                                    <strong>Rx Expires:</strong>
                                    <p>{{ expiration }}</p>
                                </b-col>
                            </b-form-row>
                        </b-col>
                    </b-form-row>
                    <b-form-row id="flag-rows" class="bordered">
                        <b-col>
                            <b-alert :show="drugsNotFilled" variant="danger" class="d-flex align-items-center my-2">
                                <i class="fas fa-times-circle mr-2"></i>
                                <strong>Drugs Not Filled &mdash; no bottle label print was recorded for this fill.
                                    Confirm the product was filled before verifying.</strong>
                            </b-alert>
                            <div v-if="prescription.dispensingItems?.length">
                                <b-alert :show="allItemsVerifiedByScan" variant="success" class="d-flex align-items-center my-2">
                                    <i class="fas fa-check-circle mr-2"></i>
                                    <strong>All product verified by scan at filling</strong>
                                </b-alert>

                                <b-alert :show="someItemsVerifiedByScan" variant="warning" class="d-flex align-items-center my-2">
                                    <i class="fas fa-exclamation-triangle mr-2"></i>
                                    <strong>Some product verified by scan at filling</strong>
                                </b-alert>

                                <b-alert :show="noItemsVerifiedByScan" variant="danger" class="d-flex align-items-center my-2">
                                    <i class="fas fa-times-circle mr-2"></i>
                                    <strong>Product not verified by scan at filling</strong>
                                </b-alert>
                                <b-table :items="prescription.dispensingItems"
                                         :fields="[
                                             { key: 'lotNumber', label: 'Lot Number' },
                                             { key: 'serialNumber', label: 'Serial Number' },
                                             { key: 'expirationDate', label: 'Expiration Date' },
                                             { key: 'quantity', label: 'Quantity' },
                                             { key: 'source', label: 'Source', class: 'source-col' }
                                         ]"
                                         :tbody-tr-class="getRowClass">
                                    <template #cell(expirationDate)="{ value }">
                                        {{ formatExpiryDate(value) }}
                                    </template>
                                    <template #cell(source)="{ value }">
                                        <b-badge v-if="value === 1" variant="success">
                                            Data Matrix
                                        </b-badge>
                                        <b-badge v-else-if="value === 2" variant="info">
                                            UPC
                                        </b-badge>
                                        <b-badge v-else-if="value === 0" variant="warning">
                                            Manual Entry
                                        </b-badge>
                                        <b-badge v-else-if="value === 4" variant="danger">
                                            External API
                                        </b-badge>
                                    </template>
                                </b-table>
                                <hr>
                                <b-form-group v-if="PartnerFillRequired" label="Partner Fill Number:">
                                    <p>{{ prescription.partnerFillNumber }}</p>
                                </b-form-group>
                            </div>
                            <!--Legacy fields on prescription-->
                            <b-form-row v-else>
                                <b-col v-show="LotNumberRequired">
                                    <strong>Lot Number:</strong>
                                    <p>{{ prescription.lotNumber }}</p>
                                </b-col>
                                <b-col>
                                    <strong>Drug Expiration Date:</strong>
                                    <p>{{ drugExpDate }}</p>
                                </b-col>
                                <b-col v-show="SerialNumberRequired">
                                    <strong>Serial Number:</strong>
                                    <p>{{ prescription.serialNumber }}</p>
                                </b-col>
                                <b-col v-show="PartnerFillRequired">
                                    <strong>Partner Fill Number:</strong>
                                    <p>{{ prescription.partnerFillNumber }}</p>
                                </b-col>
                            </b-form-row>
                        </b-col>
                    </b-form-row>
                    <b-form-row id="memo-row" class="bordered">
                        <b-col>
                            <fg-input label="Rx Memo">
                                <textarea id="rx-memo" v-model="prescription.memo" class="form-control" block-listener
                                    :disabled="disableFields"></textarea>
                            </fg-input>
                        </b-col>
                    </b-form-row>

                    <b-form-row id="questionary-answers-row" class="bordered" v-if="drugCategories.length > 0">
                        <b-col>
                            <b-row v-for="cat in drugCategories" :key="cat.id">
                                <b-col>
                                    <QuestionaryComponent :categoryID="+cat.id" :prescription="prescription"
                                        :ref="`questionary_${cat.id}`" :disable="true">
                                    </QuestionaryComponent>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-form-row>
                    <br>
                    <!-- Offline billing Plan Alert -->
                    <b-form-row id="offline-claims-alert-row">
                        <b-col>
                            <b-alert v-model="allClaimsAreOffline" variant="danger">
                                All of the claims on this prescription are billed to offline plans.
                            </b-alert>
                        </b-col>
                    </b-form-row>
                    <b-form-row id="program-row" class="bordered my-2">
                        <b-col>
                            <h6>Program:</h6>
                            <p>{{ prescription.programName }}</p>
                        </b-col>
                    </b-form-row>
                    <b-form-row id="insurance-plans-row" class="bordered my-2">
                        <b-col>
                            <h6>Patient Insurance Plans</h6>
                            <PatientPlansComponent v-model="patient" readOnly />
                        </b-col>
                    </b-form-row>
                    <b-form-row id="finance-row" class="bordered pb-2">
                        <b-col>
                            <b>Financial Information</b>
                            <InsuranceBillingComponent ref="insuranceComponent" readOnly :patient="patient"
                                :prescription="prescription" :hideMargin="pageMode == 'audit'"
                                :disabled="prescription && !prescription.isActive" @onClaimsLoaded="onClaimsLoaded" />
                        </b-col>
                    </b-form-row>
                    <!-- Hard Copy Note / Edit Image -->
                    <b-row v-if="(hasHardcopyPermission || hasEditImagePermission) && (prescription.imageID || prescription.escriptID)">
                        <b-col>
                            <h5>Hardcopy Notes</h5>
                            <fg-input v-if="hasHardcopyPermission" label="">
                                <textarea v-model="addNote" class="textarea-autosize form-control" block-listener
                                    placeholder="Enter note text to add to the prescription image..." />
                            </fg-input>
                            <b-button
                                v-if="hasHardcopyPermission"
                                :disabled="!addNote.trim()"
                                variant="success"
                                @click="openQuickAnnotate">
                                Add Hardcopy Note
                            </b-button>
                            <b-button
                                variant="warning"
                                class="ml-2"
                                @click="openEditImageModal">
                                <i class="fas fa-edit"></i> Edit Image
                            </b-button>
                            <b-button
                                v-if="allowAnnotationRevert && sourceImageId && prescription.imageID && !prescription.escriptID && (hasHardcopyPermission || hasEditImagePermission)"
                                :disabled="revertingImage"
                                variant="outline-warning"
                                class="ml-2"
                                @click="revertToOriginalImage(prescription)">
                                <i class="fas fa-undo"></i> Revert to Original
                            </b-button>
                        </b-col>
                    </b-row>

                    <!-- PDF Annotation Modal -->
                    <PdfAnnotationModal
                        modal-id="prescription-pdf-annotation"
                        :image-id="prescription.imageID"
                        :escript-id="prescription.escriptID"
                        :initial-text="quickAnnotateText"
                        :auto-place="isQuickAnnotate"
                        :prescription-id="prescription && prescription.rxNumber ? prescription.rxID : null"
                        :sibling-count="siblingScriptCount"
                        :require-permission="true"
                        @saved="onPdfAnnotationSaved"
                        @closed="onPdfAnnotationClosed" />

                    <b-card
                        v-if="drugUtilizationReviews?.items?.length || durIsSameAsBaseline || generateDurError || drugUtilizationReviews?.calculated === false"
                        class="mt-2">
                        <div class="dur-section-header glass-pane">
                            <n-switch v-if="!disableFields && !durIsSameAsBaseline" id="switch_5" v-model="durStatus"
                                color="green" :class="{ 'mr-2': true, 'selected': (verificationIndex === 5) }">
                                <template #on>
                                    <i class="fa fa-check" />
                                </template>
                                <template #off>
                                    <i class="fa fa-times" style="color:red;" />
                                </template>
                            </n-switch>
                            <h5 class="d-inline-block">DUR Results</h5>

                            <div v-if="durIsSameAsBaseline" class="text-center">
                                <div>
                                    <i class="fa fa-check" />
                                    <strong>
                                        DURs have not changed
                                    </strong>
                                </div>
                                <div v-if="!showDurs">
                                    <b-button variant="primary" @click="showDurs = true" size="sm">
                                        Show DURs
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <DURListComponent v-if="(!durIsSameAsBaseline || showDurs) && drugUtilizationReviews?.calculated"
                            :dur-items="drugUtilizationReviews.items || []"
                            :show-approval-switches="!durIsSameAsBaseline"
                            :require-explicit-dur="requireExplicitDur"
                            :require-notes="requireDurNotes"
                            :read-only="disableFields"
                            :explicit-durs-requiring-ack="explicitDursRequiringAck"
                            :verification-index="verificationIndex"
                            display-mode="card" />

                        <div v-else-if="!drugUtilizationReviews?.calculated || generateDurError"
                            class="text-danger font-weight-bold text-center">
                            <template v-if="generateDurError">
                                There was an error generating DURs for this prescription.
                            </template>
                            <template v-else-if="!drugUtilizationReviews?.calculated">
                                Unable to calculate DUR on non-GSDD product.
                                <br>
                                You must manually verify DURs.
                            </template>
                        </div>
                    </b-card>
                </b-col> <!--id="details-column"-->
            </b-form-row>

            <b-form-row>
                <b-col>
                    <!-- If an error was reported, an alert is shown-->
                    <b-alert v-if="errorIsPending" :show="errorIsPending" variant="warning">
                        <p>
                            A {{ dispenseErrorLabel }} was reported for this prescription, it needs to be accepted
                            before
                            continuing with this verification.
                            See {{ dispenseErrorLabel }} alert above for details.
                        </p>

                    </b-alert>

                    <!-- If prescription is completely unbilled, show alert -->
                    <b-alert v-if="disableForUnbilled" :show="disableForUnbilled" variant="danger">
                        <p>
                            <strong>PV2 Blocked:</strong> This prescription has not been billed.
                            Please bill to insurance, apply offline billing, or cash out the prescription before completing PV2.
                        </p>
                    </b-alert>

                    <!-- Remote worker on a patient in a restricted state: verification is view-only -->
                    <b-alert v-if="disableForRemoteWorkerState" :show="disableForRemoteWorkerState"
                        variant="warning">
                        <p class="mb-0">
                            <i class="fas fa-exclamation-triangle" />
                            This patient's state is restricted for remote workers. This prescription is view-only
                            and the verification can't be completed.
                        </p>
                    </b-alert>

                    <!-- If there is no error, buttons are shown-->
                    <PositiveIdGate
                        ref="positiveIdGate"
                        :enabled="positiveIdEnabled && positiveIdConfigLoaded && positiveIdTargetId !== null"
                        :enabled-methods="positiveIdMethods"
                        :action-type="PositiveIdActionType.RphVerification"
                        :target-entity-type="PositiveIdTargetEntityType.Prescription"
                        :target-entity-id="positiveIdTargetId || ''"
                        @verified="onPositiveIdVerifiedForSave"
                        @cancelled="onPositiveIdCancelled">
                        <template #default="{ requestVerification, verifying }">
                            <b-overlay :show="postingVerification || verifying">
                                <NewClearSaveButtons :titles="buttonTitles" :disable="disableFields" :disable-cancel="false"
                                    :disable-save="disableFields || disableVerify || positiveIdNotReady" :disable-clear="errorIsPending"
                                    @cancel-clicked="cancelClicked" @save-clicked="() => onSaveClicked(requestVerification)"
                                    @clear-clicked="openErrorDispenseModal" />
                            </b-overlay>
                        </template>
                    </PositiveIdGate>
                </b-col>
            </b-form-row>

            <DispensingErrorModalComponent v-model="dispenseError" :reviewMode="!!dispenseError.id"
                :saving="savingDispenseError"
                @onClickSave="saveDispenseError" @errorModalClosed="addListeners" @errorModalOpened="removeListeners"
                @savedNote='reloadNotesForErrorAlert' />
            <PatientRxHistoryModalComponent id="patient_this_history_modal" ref="patient_this_history_modal"
                :patient="patient" :productName="prescription.productNameShort" />
            <PatientRxHistoryModalComponent id="patient_rx_history_modal" ref="patient_rx_history_modal"
                :patient="patient" />
        </card>
        <patient-notes-modal-component v-if="prescription && prescription.patientID"
            :entity-id="prescription.patientID" />
        <doctor-notes-modal-component v-if="prescription && prescription.prescriberID"
            :entity-id="prescription.prescriberID" />
        <drug-notes-modal-component v-if="prescription && prescription.packageID" :entity-id="prescription.packageID"
            :drug-source="prescription.drugSource" />
        <ExternalTransferModal v-if="showAcceptAndTransferButton"
            :prescriptions="[prescription]"
            :store="store"
            :patient="patient"
            :accept-and-transfer-mode="true"
            :original-memo="loadedRxMemo"
            @transferred="onAcceptAndTransferComplete" />
        <DebugViewComponent ref="disable-reason-debug-view" header="">
            disableVerifyReason: {{ disableVerifyReason }}
        </DebugViewComponent>
        <TaskDetails v-if="showTaskModal"
            :id="0"
            :patient-id="patient.id"
            :prescription-id="prescription.rxID"
            @closed="showTaskModal = false"
            @saved="onTaskSaved" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.RxClarification"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="pv-request-rx-clarification-fax" @saved="updateAlerts" />

        <AlternateRxFormModal :drug="drugAsDrug" :form-type="faxFormTypeRequestEnum.AlternateRxRequest"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="pv-request-alternate-rx-fax" @saved="updateAlerts" />

        <FaxToPhysicianFormModal :drug="drugAsDrug" :custom-template="customContactPrescriberTemplate"
            :patient="patient" :prescriber="doctor" :prescription="prescription" :store="store"
            modal-id="pv-custom-prescriber-fax" @saved="updateAlerts" />

        <!-- Create Prior Authorization — same modal/flow as the Patient Dashboard's PA panel.
             Gated on canCreatePA and lazy-rendered so the PossiblePrescriptions fetch only
             happens when a permitted user actually opens it (not on every PV1 page load). -->
        <b-modal title="Create Prior Authorization" id="prior-authorization-create-modal" size="xl" lazy
            v-if="pageMode === 'pv1' && canCreatePA && patient && patient.id" hide-footer>
            <PriorAuthorizationCreate v-model="selectedPrescription" :patientId="patient.id" />
        </b-modal>

    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator';
import Axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import { isFillUnprinted } from '@/services/prescription/dateRules';
import { decideVerificationBackDestination } from '@/services/prescription/verificationNavigation';
import { formatDateAndTime } from '@/util/DateUtils';
import { Lock, LockHandler } from '@/mixins/LockHandler';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import PhoneFormatMixin from "@/mixins/PhoneFormatMixin";
import AnnotationRevertMixin from "@/mixins/AnnotationRevertMixin";
import RemoteWorkerMixin from "@/mixins/RemoteWorkerMixin";
import { getDrugImageUrls } from "@/util/DrugImage";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { shouldBlockKeyboardShortcuts } from "@/util/keyboard-utils";
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import DispensingErrorModalComponent from '@/components/DispensingError/DispensingErrorModalComponent.vue';
import DispensingErrorAlertComponent from '@/components/DispensingError/DispensingErrorAlertComponent.vue';
import Switch from "@/components/Switch.vue";
import NSwitch from "@/components/Switch.vue";
import ImageComponent from '@/components/Prescription/ImageComponent.vue';
import PatientRxHistoryModalComponent from '@/components/Patient/PatientRxHistoryModalComponent.vue';
import InsuranceBillingComponent from '@/components/InsurancePlan/InsuranceBillingComponent.vue';
import PatientPlansComponent from '@/components/Insurance/PatientPlansComponent.vue';
import QuestionaryComponent from '@/components/Questionary/QuestionaryComponent.vue';

import { DAWCode, DispenseItemSource, OriginCode, Prescription } from '@/models/Prescription';
import { DeliveryCode } from '@/models/DeliveryCode';
import { Prescriber } from '@/models/Prescriber';
import { Verification, VerificationResponse } from '@/models/Verification';
import { Patient } from '@/models/Patient';
import { Store } from '@/models/Store';
import { Drug } from '@/models/Drug/Drug';
import { Note } from '@/models/Note';
import { DispenseError } from '@/models/DispenseError';
import { GSDDPatientAllergy, GSDDPatientDiagnosis } from '@/models/GSDD';
import { Order } from '@/models/Order';
import { DrugCategory } from '@/models/DrugCategory';
import { Claim } from '@/models/Claim';
import Button from '@/components/Button.vue';
import { Printer } from '@/models/Printer';
import DrugChangeModal from "@/components/Prescription/DrugChangeModal.vue";
import CompoundIngredientsDisplay from "@/components/Compound/CompoundIngredientsDisplay.vue";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import { BvMsgBoxOptions } from "bootstrap-vue";
import { FunctionalityFlag } from "@/models/FunctionalityFlag";
import EscriptListModal from "@/components/Prescription/EscriptListModal.vue";
import PdfAnnotationModal from "@/components/PdfAnnotationModal.vue";
import { EscriptData } from '@/models/EscriptData';
import FilledImagesComponent from "@/components/Prescription/FilledImagesComponent.vue";
import HardCopyPrescriptionsComponent from "@/components/Prescription/HardCopyPrescriptionsComponent.vue";
import OtherMedicationsComponent from "@/components/Patient/OtherMedicationsComponent.vue";
import NotesList from "@/components/Note/NotesList.vue";
import { EntityType } from "@/models/EntityType";
import { PageFocusEventType, PageFocusEventClosedAction } from "@/models/PageFocusEvent";
import PageFocusEventService from "@/services/PageFocusEventService";
import DebugViewComponent from "@/components/DebugViewComponent.vue";
import DispenseErrorTypeEnum = DispenseError.DispenseErrorTypeEnum;
import { EscriptResponse } from "@/models/Escript";
import PatientCaseBanner from '@/components/Patient/PatientCaseBanner.vue';
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import PatientEducationModalComponent from "@/components/Drug/PatientEducationModalComponent.vue";
import EntityAlertsComponent from '@/components/EntityAlertsComponent.vue';
import DURListComponent from '@/components/Drug/DURListComponent.vue';
import AllergyWarningComponent from '@/components/Drug/AllergyWarningComponent.vue';
import { ButtonTitles } from "@/components/ButtonTitles";
import ExternalTransferModal from '@/components/Transfer/ExternalTransferModal.vue';
import TaskDetails from '@/components/Tasks/TaskDetails.vue';
import FaxToPhysicianFormModal from "@/components/Prescription/FaxToPhysicianFormModal.vue";
import AlternateRxFormModal from "@/components/Prescription/AlternateRxFormModal.vue";
import { FaxType as FaxToPhysicianFormType } from "@/models/PrescriberFollowUpCase";
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import PmpGatewayWidget from '@/components/Patient/PmpGatewayWidget.vue';
import NpiValidationBadge from "@/components/Prescriber/NpiValidationBadge.vue";
import PhoneCopyButton from "@/components/PhoneCopyButton.vue";
import PositiveIdGate from '@/components/Common/PositiveIdGate.vue';
import { PositiveIdActionType, PositiveIdTargetEntityType } from '@/services/PositiveIdService';
import PrescriptionStatusBadge from '@/components/Prescription/PrescriptionStatusBadge.vue';
import PriorAuthorizationCreate from '@/components/PriorAuthorization/PriorAuthorizationCreate.vue';
import CreateConsultationButton from '@/components/Consultation/CreateConsultationButton.vue';

@Component({
    name: "PrescriptionVerificationDetailsPage",
    components: {
        DrugNotesModalComponent,
        PatientEducationModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        DebugViewComponent,
        NotesList,
        OtherMedicationsComponent,
        EscriptListModal,
        PdfAnnotationModal,
        NSwitch,
        DrugChangeModal,
        CompoundIngredientsDisplay,
        SearchComponent,
        NewClearSaveButtons,
        DispensingErrorModalComponent,
        DispensingErrorAlertComponent,
        [Switch.name]: Switch,
        ImageComponent,
        PatientRxHistoryModalComponent,
        InsuranceBillingComponent,
        PatientPlansComponent,
        QuestionaryComponent,
        Button,
        FilledImagesComponent,
        HardCopyPrescriptionsComponent,
        PatientCaseBanner,
        EntityAlertsComponent,
        DURListComponent,
        AllergyWarningComponent,
        ExternalTransferModal,
        TaskDetails,
        FaxToPhysicianFormModal,
        AlternateRxFormModal,
        PmpGatewayWidget,
        NpiValidationBadge,
        PositiveIdGate,
        PhoneCopyButton,
        PrescriptionStatusBadge,
        PriorAuthorizationCreate,
        CreateConsultationButton,
    },
})
export default class PrescriptionVerificationDetailsPage extends Mixins(LockHandler, AutoModeHotkeyMixin, PhoneFormatMixin, AnnotationRevertMixin, RemoteWorkerMixin) {
    @Ref('entity-alerts') private entityAlertsComponent!: EntityAlertsComponent;
    @Ref('patient_this_history_modal') protected historyModal!: PatientRxHistoryModalComponent;
    @Ref('patient_rx_history_modal') private rxHistoryModal!: PatientRxHistoryModalComponent;
    // Flags for checkboxes
    private prescription: Prescription = new Prescription();
    // The Rx Memo as the server last handed it to us, recaptured every time a prescription loads.
    // Accept and Transfer compares the memo box against this to tell an operator edit from an
    // untouched box, so an untouched box posts no memo at all and cannot overwrite a value that
    // something else changed while this screen sat open.
    private loadedRxMemo: string | null = null;
    private prescriptionLock: Lock | null = null;
    // Holds the prescription selected inside the Create-PA modal (mirrors the Patient Dashboard flow).
    private selectedPrescription: Prescription = new Prescription();
    private patientStatus = false;
    private doctorStatus = false;
    private quantityStatus = false;
    private drugStatus = false;
    private directionStatus = false;
    private durStatus = false;
    protected drugCategories: Array<DrugCategory> = [];

    private store: Store | null = new Store();
    private patient: Patient = new Patient();
    private doctor: Prescriber = new Prescriber();
    private supervisingPhysician: Prescriber | null = null;
    private prescriptionCount: number = 0;
    private notesCount: number = 0;
    private thisHistoryCount: number = 0;
    private otherMedsCount: number = 0;
    private drug: DrugProduct | null = null;
    private order: Order | null = null;
    private dispenseError: DispenseError = new DispenseError();
    private dispenseErrors: Array<DispenseError> = [];
    protected dispenseErrorAlertButtonTitles: any = {
        accept: 'Approve',
        reject: 'Reject',
    };
    private patientAllergies: GSDDPatientAllergy[] = [];
    protected patientGSDDPatientDiagnosis: GSDDPatientDiagnosis[] = [];
    private drugUtilizationReviews: Verification.DrugUtilizationReviews | null = null;
    private generateDurError = false;
    protected ipAddress: Array<Printer> = [];
    private printerIpAddress = '';
    private drugModalVisible: boolean = false;
    private pv1ModalVisible: boolean = false;
    private programFlags: FunctionalityFlag[] = [];
    private PartnerFillRequired = false;
    private SerialNumberRequired = false;
    private LotNumberRequired = false;
    protected drugAlert: string = "";

    protected mode: string = '';
    protected isLoading: boolean = true;
    protected isLoadingDur: boolean = false;

    // Verification time tracking (SD-3120)
    private verificationLoadedAt: number = 0;
    private verificationVisibleSeconds: number = 0;
    private verificationVisibleInterval: number | null = null;
    private focusEventRecorded: boolean = false;
    protected patientAddressString = "";
    protected patientAllergyString = "";
    private isPrintingRxRf: boolean = false;

    protected requireExplicitPV1 = false;
    protected requireExplicitPV2 = false;
    protected requireExplicitDur: number | null = null;
    protected requireDurNotes = false;
    protected patientPendingAllergy = false;
    // Tenant setting: when true, pending MAC data blocks PV1 as well as PV2.
    protected blockPV1OnPendingAllergyUpdate = false;
    protected isCompletelyUnbilled = false;
    protected skipOrderVerificationAfterPV1 = false;
    // Effective value resolved server-side: the program-level "Disable PV1 Needed Warning"
    // flag suppresses only when explicitly enabled, otherwise the tenant default applies.
    // When true, the PV2 "PV1 Needed" modal is suppressed even for compounds, controls,
    // and Schedule 1/2 drugs.
    protected disablePV1NeededWarning = false;

    protected durIsSameAsBaseline: boolean = false;
    private verifiedPatientPay: number | null = null;
    protected showDurs: boolean = false;
    protected valueChangesSinceBaseline: Array<any> = [];
    protected showAllChanges: boolean = false;

    //Notes
    private addNote = '';
    private quickAnnotateText: string | null = null;
    private isQuickAnnotate = false;
    private siblingScriptCount = 0;

    protected verificationIndex = 0;
    protected allClaimsAreOffline = false;
    private escriptData: EscriptData | null = null;

    protected disableVerifyReason: string = "";
    protected activePrinter: any = null;
    protected localPrinters: any[] = [];
    protected enableAcceptAndTransfer: boolean = false;
    protected enableSupervisingPhysician: boolean = false;
    private showCrossPatientHardCopyScripts: boolean = false;
    private hideSamePatientHardCopyScripts: boolean = false;

    private postingVerification: boolean = false;

    // Positive ID gate — loaded from Configuration. `verifiedToken` holds the modal's output for one save.
    protected positiveIdEnabled: boolean = false;
    protected positiveIdMethods: string[] | null = null;
    protected positiveIdConfigLoaded: boolean = false;

    get positiveIdTargetId(): string | null {
        return this.prescription?.rxNumber ? this.prescription.rxID : null;
    }

    get positiveIdNotReady(): boolean {
        return this.positiveIdEnabled
            && (!this.positiveIdConfigLoaded || this.positiveIdTargetId === null);
    }
    private verifiedToken: string = '';
    private positiveIdModalShown: boolean = false;
    // Expose the enums to the template (template scope can't see imports directly).
    protected readonly PositiveIdActionType = PositiveIdActionType;
    protected readonly PositiveIdTargetEntityType = PositiveIdTargetEntityType;
    private savingDispenseError: boolean = false;

    protected showTaskModal: boolean = false;

    protected faxFormTypeRequestEnum = FaxToPhysicianFormType;
    protected customContactPrescriberTemplates: PrescriberContactTemplate[] = [];
    protected customContactPrescriberTemplate: PrescriberContactTemplate | null = null;

    get drugAsDrug(): Drug {
        return this.drug as Drug;
    }

    get filteredCustomContactPrescriptionTemplates() {
        return this.customContactPrescriberTemplates.filter(t => {
            if (!t.programs.length) return true;
            return t.programs.find(p => p.id === this.prescription?.programID);
        });
    }

    updateAlerts() {
        this.entityAlertsComponent?.refresh();
    }

    async getPrescriberCustomContactTemplates() {
        try {
            const resp = await Axios.get("/Prescriber/contact-templates/true");
            this.customContactPrescriberTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    blockListeners(): boolean {
        if (this.drugModalVisible) return true;
        if (this.pv1ModalVisible) return true;
        if (this.positiveIdModalShown) return true;

        // Use centralized keyboard blocking logic
        if (shouldBlockKeyboardShortcuts()) return true;

        // Keep the block-listener attribute check for backwards compatibility
        // and explicit opt-in cases
        const blockersElements = document.querySelectorAll("*[block-listener]");
        for (const el of blockersElements) {
            if (document.activeElement === el) return true;
        }

        return false;
    }

    keypressListener(e: any) {
        if (this.blockListeners()) return;

        //Check/Uncheck verifications with 'V' key
        if (e.code === 'KeyV' && this.showExplicitVerification && this.prescription?.rxNumber) {
            this.scrollToFocusedSwith();
            switch (this.verificationIndex) {
                //case 0: this.storeStatus = !this.storeStatus; break;
                case 0:
                    this.patientStatus = !this.patientStatus;
                    break;
                case 1:
                    this.doctorStatus = !this.doctorStatus;
                    break;
                case 2:
                    this.drugStatus = !this.drugStatus;
                    break;
                case 3:
                    this.quantityStatus = !this.quantityStatus;
                    break;
                case 4:
                    this.directionStatus = !this.directionStatus;
                    break;
                case 5:
                    this.durStatus = !this.durStatus;
                    break;
                default:
                    // Handle explicit DUR switches (index 6+)
                    const durIndex = this.verificationIndex - 6;
                    if (durIndex >= 0 && durIndex < this.explicitDursRequiringAck.length) {
                        const dur = this.explicitDursRequiringAck[durIndex];
                        dur.status = !dur.status;
                    }
                    break;
            }
            const indexLimit = !this.drugUtilizationReviews?.items?.length ? 4 : 5 + this.explicitDursRequiringAck.length;
            this.verificationIndex = this.verificationIndex > indexLimit - 1 ? 0 : this.verificationIndex + 1;
            e.preventDefault();
            this.scrollToFocusedSwith();
            return;
        }
    }

    keydownListener(e: KeyboardEvent) {
        if (this.blockListeners()) return;
        if (e.code === "F5") e.preventDefault();
    }

    keyupListener(e: KeyboardEvent) {
        if (this.blockListeners()) return;

        switch (e.code) {
            case 'NumpadEnter':
            case 'Enter':
                if (this.isLoadingDur) break;
                if (this.durChecked || !this.drugUtilizationReviews?.items?.length) {
                    // Route Enter through the same PositiveId gate as the Save button. When the
                    // toggle is off the gate is a pass-through, so behavior is unchanged.
                    this.triggerVerificationSave();
                } else {
                    this.openErrorDispenseModal();
                }
                break;
            case 'F5':
                this.rxHistoryModal.openRxHistory();
                break;
        }
    }

    private triggerVerificationSave(): void {
        if (this.postingVerification) return;
        if (this.disableVerify) {
            // Save button is UI-disabled in this state; Enter has no such guard, so mirror the
            // notification processNextInBatch would have shown without first prompting for a tap.
            const reason = this.disableVerifyReason || "Complete all required verifications before approving";
            this.$notification(NotificationOptions.notificationOptionsPreset(reason,
                NotificationOptions.NotificationTypes.warning));
            return;
        }
        const gate = this.$refs.positiveIdGate as { requestVerification?: () => void } | undefined;
        if (gate?.requestVerification) {
            if (this.positiveIdEnabled) this.positiveIdModalShown = true;
            gate.requestVerification();
        } else {
            // Defensive: gate not mounted (template branch hidden by an error). Fall back to the
            // legacy direct save — this is the pre-PositiveId behavior and is correct when the
            // toggle is off.
            this.processNextInBatch();
        }
    }

    onSaveClicked(requestVerification: () => void): void {
        if (this.positiveIdEnabled) this.positiveIdModalShown = true;
        requestVerification();
    }

    onPositiveIdCancelled(): void {
        this.positiveIdModalShown = false;
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        this.removeListeners();
        this.recordPageFocusTime(PageFocusEventClosedAction.Abandoned,
            this.verificationLoadedAt > 0 ? Math.round((performance.now() - this.verificationLoadedAt) / 1000) : null);
        this.stopVisibleTimer();
    }

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.addListeners();
        this.getLocalPrinters();
        this.fetchConfiguration();
    }

    addListeners() {
        document.addEventListener("keypress", this.keypressListener);
        document.addEventListener("keydown", this.keydownListener);
        document.addEventListener("keyup", this.keyupListener);
    }

    removeListeners() {
        document.removeEventListener("keypress", this.keypressListener);
        document.removeEventListener("keydown", this.keydownListener);
        document.removeEventListener("keyup", this.keyupListener);
    }

    private startVisibleTimer() {
        this.stopVisibleTimer();
        this.verificationVisibleSeconds = 0;
        this.verificationVisibleInterval = window.setInterval(() => {
            if (document.hasFocus() && !document.hidden) {
                this.verificationVisibleSeconds++;
            }
        }, 1000);
    }

    private stopVisibleTimer() {
        if (this.verificationVisibleInterval !== null) {
            clearInterval(this.verificationVisibleInterval);
            this.verificationVisibleInterval = null;
        }
    }

    private recordPageFocusTime(closedAction: PageFocusEventClosedAction, totalDurationSeconds: number | null) {
        if (this.focusEventRecorded || this.verificationLoadedAt <= 0 || !this.prescription?.rxNumber) return;

        const pageType = this.pageMode == 'pv1' ? PageFocusEventType.PV1
            : this.pageMode == 'pv2' ? PageFocusEventType.PV2
            : null;
        if (pageType === null) return;

        this.focusEventRecorded = true;
        PageFocusEventService.record({
            storeID: this.prescription.storeID,
            rxNumber: this.prescription.rxNumber,
            rfNumber: this.prescription.rfNumber,
            pageType,
            closedAction,
            visibleDurationSeconds: this.verificationVisibleSeconds,
            totalDurationSeconds,
        });
    }

    scrollToFocusedSwith() {
        if (!this.verificationIndex) return;

        let swithID: string;
        if (this.verificationIndex <= 5) {
            swithID = 'switch_' + this.verificationIndex;
        } else {
            const durIndex = this.verificationIndex - 6;
            swithID = 'switch_dur_' + durIndex;
        }

        const switchElement = document.getElementById(swithID);
        if (switchElement) {
            switchElement.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });
        }
    }

    get showIpInput() {
        return !this.activePrinter;
    }

    get showAcceptAndTransferButton(): boolean {
        return this.enableAcceptAndTransfer &&
               !!this.prescription?.rxNumber &&
               !this.prescription?.pV1ID;
    }

    get disableAcceptAndTransferButton(): boolean {
        return !!this.acceptAndTransferDisabledReason;
    }

    // Accept and Transfer auto-stamps a PV1, so it blocks on pending MAC data exactly as Verify does.
    // Returning the reason rather than a bare flag lets the button explain itself in a tooltip.
    get acceptAndTransferDisabledReason(): string {
        if (this.disableForRemoteWorkerState) {
            return "Restricted State for Remote Workers";
        }
        if (this.drugUtilizationReviews?.items?.length && !this.durChecked) {
            return "DURs Not Reviewed";
        }
        if (this.disableForAllergy) {
            return this.pageMode == "pv1"
                ? "Patient Has Pending Medication, Allergy, or Condition Updates"
                : "Patient Pending Allergy";
        }
        return "";
    }

    get buttonTitles(): ButtonTitles {
        const cancel = this.routeDispenseErrorId ? "Back to " + this.dispenseErrorLabel + " List"
            : this.routeOrderId ? "Back to Order Verification"
                : "";
        const save = (this.hasBatchPrescriptions() || this.$store.autoModeEnabled) ? "Accept and Next" : "Accept";
        return {
            new: "",
            clear: "Reject",
            save: save,
            cancel: cancel,
        };
    }

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.getPrescription(this.routeRxId);
        this.getPrescriberCustomContactTemplates();
        this.isLoading = true;
        window.scroll(0, 0);
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        // Only handle changes when staying on the same route (PV1 to PV1)
        // (navigating away will trigger beforeDestroy which handles cleanup)
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        // Release all locks from previous prescription before loading new one
        await this.clearAllLocks();

        // Calculate old and new rxIds from route params
        const getRouteRxId = (route: any) => {
            if (route.params.id) return route.params.id;

            const storeID = Number(route.params.storeID);
            const rxNumber = Number(route.params.rxNumber);
            const rfNumber = Number(route.params.rfNumber);

            if (!isNaN(storeID) && !isNaN(rxNumber) && !isNaN(rfNumber)) {
                return `${storeID}-${rxNumber}-${rfNumber}`;
            }
            return null;
        };

        const oldRxId = getRouteRxId(oldRoute);
        const newRxId = getRouteRxId(newRoute);

        // If rxId changed, reload the prescription
        if (oldRxId !== newRxId && newRxId) {
            console.log('Route prescription params changed, loading new prescription:', newRxId);
            this.recordPageFocusTime(PageFocusEventClosedAction.Abandoned,
                this.verificationLoadedAt > 0 ? Math.round((performance.now() - this.verificationLoadedAt) / 1000) : null);
            this.focusEventRecorded = false;
            this.isLoading = true;
            this.getPrescription(newRxId);
            window.scroll(0, 0);
        }
    }

    drugChangeSuccess() {
        this.drugModalVisible = false;
        this.$router.go(0);
    }

    hasBatchPrescriptions(): boolean {
        // Auto mode is distinct from batch mode - don't show batch counter for auto mode
        if (this.$store.autoModeEnabled) return false;
        return this.remainingBatchCount() > 0;
    }

    remainingBatchCount(): number {
        const count = this.$store.pv1BatchPrescriptions?.length ?? 0;
        return count;
    }

    async navigateToNextInBatch() {
        // Navigate to the next prescription in batch without verifying current one

        // Note: Auto mode handling now happens in postVerification() success handler
        // This method will only handle manual batch processing from here on

        while (this.hasBatchPrescriptions()) {
            const nextPrescription = this.$store.pv1BatchPrescriptions?.shift();

            if (!nextPrescription) break;

            // SD-2057: Check verification status server-side before navigating
            // to avoid stale data, ghost history entries, and loading flicker
            try {
                const response = await Axios.get<VerificationResponse>(
                    `/Prescription/${nextPrescription.rxID}/verification`);
                const freshPrescription = response.data.prescription;
                const alreadyVerified = this.pageMode === 'pv1'
                    ? !!freshPrescription?.pV1ID
                    : this.pageMode === 'pv2'
                        ? !!freshPrescription?.pV2ID
                        : false;

                if (alreadyVerified) {
                    this.$notification(NotificationOptions.warning(
                        `Rx ${nextPrescription.rxNumber}-${nextPrescription.rfNumber} was already verified — skipping.`));
                    continue;
                }
            } catch {
                // If the check fails, proceed with navigation — the user can handle it normally
            }

            this.$router.push({
                name: 'PV1',
                params: {
                    rxId: nextPrescription?.rxID?.toString() || '',
                    storeID: nextPrescription?.storeID?.toString() || '',
                    rxNumber: nextPrescription?.rxNumber?.toString() || '',
                    rfNumber: nextPrescription?.rfNumber?.toString() || '',
                },
                query: {
                    batch: this.$route.query.batch ?? 'true',
                },
            });
            this.isLoading = true;
            return;
        }

        this.exitBatchMode();
        this.backToPreviousPage(false);
    }

    // Captures the verified token from PositiveIdGate and continues with the existing save flow.
    // When positive-ID is disabled in Configuration the gate is a pass-through and verifiedToken is empty;
    // postVerification sends no header in that case and the backend filter no-ops too.
    async onPositiveIdVerifiedForSave(payload: { verifiedToken: string }): Promise<void> {
        this.positiveIdModalShown = false;
        this.verifiedToken = payload.verifiedToken;
        await this.processNextInBatch();
    }

    async processNextInBatch() {
        if (this.disableVerify) {
            const reason = this.disableVerifyReason || "Complete all required verifications before approving";
            this.$notification(NotificationOptions.notificationOptionsPreset(reason,
                NotificationOptions.NotificationTypes.warning));
            return;
        }

        await this.verifyClicked();

        if (this.hasBatchPrescriptions()) {
            await this.navigateToNextInBatch();
        }
    }

    exitBatchMode() {
        this.$store.pv1BatchPrescriptions = [];
    }

    async handleAutoModeAdvance() {
        // Use shared helper from mixin which handles exit logic
        const hasNextItem = await this.handleAutoModeNextItem();

        if (!hasNextItem) {
            // PV1-specific cleanup when no more items
            this.exitBatchMode();
            this.backToPreviousPage(false);
        }
    }

    drugChangeFail(message: string) {
        this.drugModalVisible = false;
        this.dispenseError = new DispenseError(this.prescription);
        this.dispenseError.errorType = DispenseErrorTypeEnum.WrongNDC;
        this.dispenseError.reason = message;
        this.openErrorDispenseModal();
    }

    drugChangeComplete(response: AxiosResponse, newDrug: string) {
        console.log("newDrug: ", newDrug);

        function isAxiosError(item: any): item is AxiosError {
            // noinspection JSIncompatibleTypesComparison
            return (item as AxiosError).isAxiosError !== undefined;
        }

        if (isAxiosError(response)) {
            const err: AxiosError = response as AxiosError;
            const errorMessage = (err.response?.data as any).message + '\n' + newDrug;
            this.drugChangeFail(errorMessage);
        } else if (response?.status == 200) {
            this.drugChangeSuccess();
        } else {
            console.warn("Bad place to have gotten into!");
        }
    }

    setIsLoading(_val: Patient) {
        this.isLoading = true;
    }

    get doctorAddressString(): string {
        if (!this.doctor) return "";
        return (this.doctor.address1 + (this.doctor.address2 ? ', ' + this.doctor.address2 : '') + ', '
            + this.doctor.addressCity + ', ' + this.doctor.addressState + ' ' + this.doctor.addressZip).toString();
    }

    @Watch("prescription.supervisingPhysicianId", { immediate: true })
    onSupervisingPhysicianIdChanged(newId: number | null | undefined) {
        if (!newId) {
            this.supervisingPhysician = null;
            return;
        }
        this.fetchSupervisingPhysician(newId);
    }

    async fetchSupervisingPhysician(id: number) {
        try {
            const resp = await Axios.get<Prescriber>(`/Prescriber/${id}`);
            this.supervisingPhysician = resp.data;
        } catch (err) {
            console.error(err);
            this.supervisingPhysician = null;
        }
    }

    get showSupervisingPhysician(): boolean {
        return !!(this.enableSupervisingPhysician && this.doctor?.requiresSupervisingPhysician && this.supervisingPhysician);
    }

    get supervisingPhysicianDisplay(): string {
        const sp = this.supervisingPhysician;
        if (!sp) return "";
        const lastName = sp.lastName?.trim() ?? "";
        const firstName = sp.firstName?.trim() ?? "";
        const suffix = sp.suffix?.trim() ?? "";
        const name = [lastName, firstName].filter(Boolean).join(", ");
        return suffix ? `${name}, ${suffix}` : name;
    }

    // used to calculate if the patient is 12 & under
    get patientYearsOld(): number {
        return moment().diff(moment(this.patient.dateOfBirth), "years");
    }

    get patientIsUnder12(): boolean {
        return this.patientYearsOld <= 12;
    }

    get patientDobString(): string {
        if (!this.patient?.dateOfBirth) return 'unknown';
        let dobString = `${this.formattedLocalDate(this.patient.dateOfBirth)} (${this.patientYearsOld} years old)`;
        if (this.patientIsUnder12) dobString = " ⚠︝ " + dobString + " ⚠︝ ";
        return dobString;
    }

    get upc(): string {
        if (this.drug?.source != DrugSource.GsddFederal) return '';
        const drug = this.drug as Drug;
        const idents = drug?.packageDetail?.packageIdentifier?.filter((pi: any) => pi.identifierType == 8);
        return idents?.length > 0 ? idents[0].identifier : '';
    }

    get allItemsVerifiedByScan(): boolean {
        if (!this.prescription?.dispensingItems?.length) return false;
        return this.prescription.dispensingItems.every((item: any) =>
            item.source === DispenseItemSource.DataMatrix ||
            item.source === DispenseItemSource.UPC ||
            item.source === DispenseItemSource.Parata
        );
    }

    get someItemsVerifiedByScan(): boolean {
        if (!this.prescription?.dispensingItems?.length) return false;
        const scannedItems = this.prescription.dispensingItems.filter((item: any) =>
            item.source === DispenseItemSource.DataMatrix ||
            item.source === DispenseItemSource.UPC ||
            item.source === DispenseItemSource.Parata
        );
        return scannedItems.length > 0 && scannedItems.length < this.prescription.dispensingItems.length;
    }

    get noItemsVerifiedByScan(): boolean {
        if (!this.prescription?.dispensingItems?.length) return false;
        return this.prescription.dispensingItems.every((item: any) =>
            item.source !== DispenseItemSource.DataMatrix &&
            item.source !== DispenseItemSource.UPC &&
            item.source !== DispenseItemSource.Parata
        );
    }

    get drugsNotFilled(): boolean {
        if (this.pageMode !== 'pv2') return false;
        return isFillUnprinted(this.prescription);
    }

    getRowClass(item: any, type: string) {
        if (type === 'row') {
            switch (item.source) {
                case 1: // DataMatrix
                    return 'source-datamatrix';
                case 2: // UPC
                    return 'source-upc';
                case 3: // Parata
                    return 'source-parata';
                case 4: // External API
                    return 'source-external';
                case 0: // Manual
                default:
                    return 'source-manual';
            }
        }
        return '';
    }

    get pageMode() {
        let res = '';
        if (this.$router.currentRoute.name == 'PV2') {
            res = "pv2";
        }
        if (this.$router.currentRoute.name == 'PV1') {
            res = "pv1";
        }
        if (this.$router.currentRoute.name == 'Audit') {
            res = "audit";
            document.title = `Audit ${this.routeRxId}`;
        }
        return res;
    }

    get routeRxId(): string | any {
        let res = this.$route.params.id;
        if (!res) {
            const storeID = Number(this.$route.params.storeID);
            const rxNumber = Number(this.$route.params.rxNumber);
            const rfNumber = Number(this.$route.params.rfNumber);
            if (!isNaN(storeID) && !isNaN(rxNumber) && !isNaN(rfNumber)) {
                res = `${storeID}-${rxNumber}-${rfNumber}`;
            }
        }
        if (res.length == 0 || res === '0-0-0') {
            this.backToPreviousPage(false);
        }
        return res;
    }

    get routeOrderId(): number {
        let res = 0;
        if (this.$route.params.orderID) {
            res = Number(this.$route.params.orderID);
        }

        return res;
    }

    get routeDispenseErrorId(): number | null {
        let res = null;
        if (this.$route.params.dispenseErrorId) {
            res = Number(this.$route.params.dispenseErrorId);
        } else if (this.dispenseError?.id) {
            res = Number(this.dispenseError?.id);
        }
        return res;
    }

    get disableFields(): boolean {
        //return !(this.prescriptionLock || (this.prescription && !this.prescription.rxNumber));
        const lockedForUser = !this.prescriptionLock;
        const noEntityLoaded = !(this.prescription?.rxNumber);

        // Disable fields if
        // ** it's locked for the user
        // ** or if there is no object loaded and
        // ** or the patient's state is closed to this remote worker, which makes the screen genuinely
        //    view-only rather than just un-completable
        return (lockedForUser || noEntityLoaded || this.disableForRemoteWorkerState);
    }

    get errorIsPending(): boolean {
        return !!(this.dispenseError?.id && !this.dispenseError?.accepted);
    }

    get disableForAllergy(): boolean {
        if (!this.patientPendingAllergy) return false;
        // PV2 always blocks on pending MAC data; PV1 blocks only when the tenant has opted in.
        return this.pageMode == "pv2" || (this.pageMode == "pv1" && this.blockPV1OnPendingAllergyUpdate);
    }

    get disableForUnbilled(): boolean {
        if (this.pageMode != "pv2") return false;
        return this.isCompletelyUnbilled;
    }

    /**
     * A remote worker may not complete a verification for a patient residing in a restricted state.
     * Mirrors the API gate on POST prescription/{id}/verification, which applies to every PVNbr, so
     * the script is a clean dead end instead of one the user works and the save then rejects.
     */
    get disableForRemoteWorkerState(): boolean {
        return this.isRemoteWorkBlockedForPatient(this.patient);
    }

    get disableVerify(): boolean {
        this.disableVerifyReason = "";
        // Checked first: no amount of reviewing the script makes this user eligible to verify it.
        if (this.disableForRemoteWorkerState) {
            this.disableVerifyReason = "Restricted State for Remote Workers";
            return true;
        }
        if (this.errorIsPending) {
            this.disableVerifyReason = "Pending Error";
            return true;
        }
        if (this.disableForAllergy) {
            this.disableVerifyReason = this.pageMode == "pv1"
                ? "Patient Has Pending Medication, Allergy, or Condition Updates"
                : "Patient Pending Allergy";
            return true;
        }
        if (this.disableForUnbilled) {
            this.disableVerifyReason = "Prescription Not Billed";
            return true;
        }

        const validChecks = this.patientStatus
            && this.doctorStatus
            && this.quantityStatus
            && this.drugStatus
            && this.directionStatus;

        if (!this.drugUtilizationReviews?.items?.length) return !validChecks;
        return !(validChecks && this.durChecked);
    }

    get durChecked(): boolean {
        if (this.isLoadingDur) return false;
        if (!this.drugUtilizationReviews?.items?.length || this.durIsSameAsBaseline) return true;
        if (this.requireExplicitDur !== 0 && !this.requireExplicitDur && this.durStatus) return true;
        if (!this.durStatus) return false;

        return this.drugUtilizationReviews.items.every(i => {
            if (!i.active) return true;
            const requiresApproval = i.severity >= this.requireExplicitDur!;
            if (!requiresApproval) return true;
            if (!i.status) return false;
            if (this.requireDurNotes && !i.notes?.trim()) return false;
            return true;
        });
    }

    get explicitDursRequiringAck(): Verification.DURItem[] {
        if (!this.drugUtilizationReviews?.items?.length) return [];
        if (this.requireExplicitDur === null || this.requireExplicitDur === undefined) return [];
        if (this.durIsSameAsBaseline) return [];

        return this.drugUtilizationReviews.items.filter(i =>
            i.active && i.severity >= this.requireExplicitDur!
        );
    }

    get doctorNameLabel(): string {
        return this.doctor.displayNamesForPerson();
    }

    get deliveryCodeText(): string {
        if (!this.prescription) return "";
        return `${this.prescription.deliveryCode} - ${DeliveryCode[this.prescription.deliveryCode]}`;
    }

    get originCodeText(): string {
        if (!this.prescription) return "";
        return `${this.prescription.originCode} - ${OriginCode[this.prescription.originCode]}`;
    }

    get dawCodeText(): string {
        if (!this.prescription) return "";
        return `${this.prescription.dawCode} - ${DAWCode[this.prescription.dawCode]}`;
    }

    get requiresPV1(): boolean {
        return this.prescription.isPV1Required || (this.drug instanceof Drug && this.drug.isControl) || (this.drug instanceof CompoundDrug);
    }

    printByRxRf() {
        if (!this.isPrintingRxRf) {

            this.isPrintingRxRf = true;
            type PrintedViewModel = { printedLabels: number, badLabels: { label: any, error: string }[] };
            Axios.get<PrintedViewModel>(`/Print/${this.prescription.rxID}/rx-pated`)
                 .then(res => {
                     if (res.data.printedLabels > 0 && res.data.badLabels?.length == 0) {
                         const successMessage = `PatEd for Rx ${this.prescription.rxID} was successfully printed`;
                         this.$notification(NotificationOptions.notificationOptionsPreset(successMessage, NotificationOptions.NotificationTypes.success));
                     }
                 })
                 .catch(err => {
                     this.$notification(NotificationOptions.error(err));
                     console.error("Error while pulling printing Rx", {
                         err,
                         response: err?.response,
                     });
                 })
                 .finally(() => {
                     this.isPrintingRxRf = false;
                 });
        }
    }

    @Watch('prescription')
    async prescriptionUpdate(value: Prescription | null, oldValue: Prescription | null) {
        if (!value || !value.rxNumber) return;

        if (oldValue && oldValue.storeID && oldValue.rxNumber && oldValue.rfNumber) {
            if (oldValue.storeID != value.storeID
                || oldValue.rxNumber != value.rxNumber
                || oldValue.rfNumber != value.rfNumber) {
                await this.fetchPrescription(value);
            }
        }

        if (value.imageID && value.imageID !== oldValue?.imageID) {
            this.fetchSourceImageId(value.imageID);
        } else if (!value.imageID) {
            this.clearSourceImageId();
        }
    }

    fetchDrugAlert(drug: DrugProduct) {
        Axios.get(`/Drug/${drug.drugId}/Alert`, {
            params: {
                getProgram: true,
                drugSource: drug.source,
            },
        })
             .then((response) => {
                 this.drugAlert = response.data;
             })
             .catch((error) => {
                 console.error("Error while getting drug alert", {
                     error,
                     response: error?.response,
                 });
             });
    }

    fetchPrescription(prescription: Prescription) {
        const rxID = prescription.rxID;
        return this.getPrescription(rxID);
    }

    getPrescription(rxID: string) {
        // Clear stale escript data from previous prescription
        this.escriptData = null;
        this.dispenseError = new DispenseError();
        this.durStatus = false;
        this.prescription = new Prescription();
        this.loadedRxMemo = null;
        // Reset the V-key section marker to the top for the new Rx (component is reused, not remounted)
        this.verificationIndex = 0;
        return this.addLockWithURL(`/Prescription/${rxID}/Lock`, 60000)
                   .then(result => {
                       this.prescriptionLock = result;
                   })
                   .catch(error => {
                       if (error.response && error.response.status == 418) {
                           this.showLockedMessage(
                               EntityType.Prescription,
                               rxID,
                               rxID,
                               error.response.data,
                           );
                       }
                   })
                   .finally(() => {
                       // If it's locked should we even allow verification to view anything?
                       // this.patientStatus = this.doctorStatus = this.quantityStatus = this.drugStatus = false;

                       Axios.get<VerificationResponse>(`/Prescription/${rxID}/verification`)
                            .then(response => {
                                const data = response.data;

                                this.prescription = new Prescription(data.prescription);
                                this.loadedRxMemo = this.prescription.memo ?? null;
                                this.patient = new Patient(data.patient);

                                this.patientAddressString = this.buildAddressString(this.patient);
                                this.doctor = new Prescriber(data.prescriber);
                                this.store = new Store(data.store);
                                this.order = new Order(data.order);
                                this.programFlags = data.programFlags;
                                this.prescriptionCount = response.data.prescriptionCount || 0;
                                this.notesCount = response.data.notesCount || 0;
                                this.thisHistoryCount = response.data.thisHistoryCount || 0;
                                this.otherMedsCount = response.data.otherMedsCount || 0;
                                this.inventoryOnHand = response.data.inventoryOnHand ?? 0;
                                this.inventoryAllocated = response.data.inventoryAllocated ?? 0;

                                this.PartnerFillRequired = this.programFlags.find((m: any) => m.name == "Partner Fill Number Required")?.value ?? false;

                                if (this.pageMode == 'pv2') {
                                    this.SerialNumberRequired = this.programFlags.find((m: any) => m.name == "Serial Number Required")?.value ?? false;
                                    this.LotNumberRequired = this.programFlags.find((m: any) => m.name == "Lot Number Required")?.value ?? false;
                                } else {
                                    this.SerialNumberRequired = false;
                                    this.LotNumberRequired = false;
                                }

                                Axios.get(`/Allergy/GetGSDDAllergiesForPatient/${this.patient.id}`)
                                     .then(response => {
                                         this.patientAllergies = response.data;
                                         this.patientAllergyString = this.buildAllergyString(this.patientAllergies);
                                         if (this.patient.nkda) {
                                             this.patientAllergyString += 'NKDA';
                                         }
                                     }).catch(error => {
                                    console.error("Error while loading allergies", {
                                        error,
                                        response: error?.response,
                                    });
                                });

                                Axios.get(`/Diagnosis/Patient/${this.patient.id}`)
                                     .then(response => {
                                         this.patientGSDDPatientDiagnosis = response.data;
                                     }).catch(error => {
                                    console.error("Error while loading icd10 codes", {
                                        error,
                                        response: error?.response,
                                    });
                                });

                                this.generateDurError = false;
                                this.drugUtilizationReviews = null;

                                this.isLoadingDur = true;
                                Axios.post(`/Prescription/${rxID}/GenerateDURs`)
                                     .then(response => {
                                         this.drugUtilizationReviews = response.data.durs;

                                         // Ensure DUR items have reactive status and notes properties
                                         if (this.drugUtilizationReviews?.items) {
                                             this.drugUtilizationReviews.items.forEach(dur => {
                                                 if (dur.status === undefined) {
                                                     this.$set(dur, 'status', false);
                                                 }
                                                 if (dur.notes === undefined) {
                                                     this.$set(dur, 'notes', '');
                                                 }
                                             });
                                         }

                                         this.valueChangesSinceBaseline = response.data.changes;
                                         this.durIsSameAsBaseline = response.data.isDURSameAsBaseline;
                                     })
                                     .catch(error => {
                                         this.generateDurError = true;
                                         console.error("Error while pulling DUR", {
                                             error,
                                             response: error?.response,
                                         });
                                     })
                                     .finally(() => {
                                         this.isLoadingDur = false;
                                     });

                                Axios.get<EscriptResponse>(`/api/Escript/prescription/${this.prescription.rxID}`)
                                     .then(resp => {
                                         if (resp && resp.data) this.prescription.escriptID = +resp.data.eScript.id;
                                     })
                                     .finally(() => {
                                         Axios.get<EscriptData>(`/Escript/Parsed/${this.prescription.escriptID}`)
                                              .then(response => {
                                                  this.escriptData = new EscriptData(response.data);
                                              })
                                              .catch(error => {
                                                  console.warn("Error attempting to fetch escript",
                                                      {
                                                          error,
                                                          response: error?.response,
                                                      });
                                              });
                                     });

                                //Pull all errors reported for this prescription
                                this.dispenseErrors = data.dispenseErrors.map(err => Object.assign(new DispenseError(), err));

                                //Take the current one.
                                if (this.dispenseErrors?.some(err => !err.accepted)) {
                                    this.dispenseError = this.dispenseErrors.filter(err => !err.accepted)[0];
                                }

                                //If there is no active error, set the new one with the RxID
                                if (!this.dispenseError.id) {
                                    this.dispenseError.storeID = this.prescription.storeID;
                                    this.dispenseError.rxNumber = this.prescription.rxNumber;
                                    this.dispenseError.rfNumber = this.prescription.rfNumber;
                                }

                                if (data.drug.source == DrugSource.GsddFederal) {
                                    this.drug = new Drug(null, data.drug);
                                    this.drugDetails = new Drug(null, data.drug);
                                }
                                if (data.drug.source == DrugSource.NonGsddProduct) {
                                    this.drug = new NonGSDDDrug(null, data.drug);
                                    this.drugDetails = new NonGSDDDrug(null, data.drug);
                                }
                                if (data.drug.source == DrugSource.CompoundProduct) {
                                    this.drug = new CompoundDrug(null, data.drug);
                                    this.drugDetails = new CompoundDrug(null, data.drug);
                                }

                                if (this.drug != null) this.fetchDrugAlert(this.drug);
                                this.fetchDrugCategory(this.drug?.drugId);

                                //const verification = data.verifications.find(p => this.prescription && this.prescription.pV2ID ? p.id == this.prescription.pV2ID : this.prescription && this.prescription.pV1ID ? p.id == this.prescription.pV1ID : p.id != null);
                                let verification: Verification | null = {} as Verification;
                                if (this.pageMode == 'pv2') {
                                    verification = data.verifications.find(v => v.id == (this.prescription?.pV2ID || 0)) || null;
                                } else if (this.pageMode == 'pv1') {
                                    verification = data.verifications.find(v => v.id == (this.prescription?.pV1ID || 0)) || null;
                                }

                                this.requireExplicitPV1 = data.requireExplicitPV1;
                                this.requireExplicitPV2 = data.requireExplicitPV2;
                                this.requireExplicitDur = data.requireExplicitDur;
                                this.requireDurNotes = data.requireDurNotes;
                                this.patientPendingAllergy = data.patientPendingAllergy;
                                this.blockPV1OnPendingAllergyUpdate = data.blockPV1OnPendingAllergyUpdate;
                                this.isCompletelyUnbilled = data.isCompletelyUnbilled;
                                this.disablePV1NeededWarning = data.disablePV1NeededWarning;

                                if (verification) {
                                    this.patientStatus = !this.showExplicitVerification || verification.patient;
                                    this.doctorStatus = !this.showExplicitVerification || verification.prescriber;
                                    this.quantityStatus = !this.showExplicitVerification || verification.quantity;
                                    this.directionStatus = !this.showExplicitVerification || verification.direction;
                                    this.drugStatus = !this.showExplicitVerification || verification.drug;
                                } else {
                                    this.patientStatus = !this.showExplicitVerification;
                                    this.doctorStatus = !this.showExplicitVerification;
                                    this.quantityStatus = !this.showExplicitVerification;
                                    this.directionStatus = !this.showExplicitVerification;
                                    this.drugStatus = !this.showExplicitVerification;
                                }
                            })
                            .catch(error => {
                                console.error('Error while loading verification details.', {
                                    error,
                                    response: error?.response,
                                });
                            })
                            .finally(() => {
                                if (this.pageMode == "pv2") {
                                    if (this.requiresPV1 && !this.prescription.pV1ID && !this.disablePV1NeededWarning) {
                                        const opt: BvMsgBoxOptions = {
                                            title: "PV1 Needed",
                                            size: "md",
                                            buttonSize: "md",
                                            okTitle: "OK",
                                            hideHeaderClose: true,
                                            centered: true,
                                        };
                                        const msg = "PV1 was not performed for this prescription. " +
                                            "Ensure you have done all the checks required before dispensing.";
                                        this.pv1ModalVisible = true;
                                        this.$bvModal.msgBoxOk(msg, opt).finally(() => {
                                            this.pv1ModalVisible = false;
                                        });
                                    }
                                }
                                this.isLoading = false;
                                this.verificationLoadedAt = performance.now();
                                this.startVisibleTimer();
                            });
                   });
    }

    // PDF Annotation methods
    openEditImageModal() {
        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to edit."));
            return;
        }

        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;
        this.$bvModal.show('prescription-pdf-annotation');
    }

    openQuickAnnotate() {
        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        if (!this.addNote.trim()) {
            this.$notification(NotificationOptions.error("Please enter a note to add."));
            return;
        }

        // Set up quick annotate with the note text
        this.quickAnnotateText = this.addNote.trim();
        this.isQuickAnnotate = true;

        this.$bvModal.show('prescription-pdf-annotation');
    }

    async onPdfAnnotationSaved(payload: {
        oldImageId: number | null;
        newImageId: number;
        escriptId: number | null;
        saveToAll: boolean;
        orphanUpdates?: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }>;
    }) {
        // Clear the quick annotate state and the note textarea
        if (this.isQuickAnnotate) {
            this.addNote = "";
            this.quickAnnotateText = null;
            this.isQuickAnnotate = false;
        }

        // Handle eScript annotation - just refresh the display (no imageID change needed)
        if (payload.escriptId) {
            if (this.$refs.prescriptionImage) {
                (this.$refs.prescriptionImage as any).fetchPrescriptionImageSource(`escript/${payload.escriptId}/image`);
            }
            this.$notification(NotificationOptions.success("eScript annotation saved successfully."));
            return;
        }

        // Handle regular image annotation - update ONLY the image ID
        if (payload.newImageId && this.prescription) {
            try {
                if (payload.saveToAll && payload.oldImageId) {
                    await Axios.put("/Prescription/update-sibling-images", {
                        currentImageId: payload.oldImageId,
                        newImageId: payload.newImageId,
                    });

                    const count = this.siblingScriptCount;
                    this.$notification(NotificationOptions.success(
                        `Annotated PDF saved and applied to ${count} prescription${count !== 1 ? 's' : ''}.`
                    ));

                    // Update individually-annotated prescriptions with re-annotated images
                    if (payload.orphanUpdates?.length) {
                        try {
                            for (const update of payload.orphanUpdates) {
                                await Axios.post("/Prescription/updateImage", {
                                    storeID: update.storeID,
                                    rxNumber: update.rxNumber,
                                    rfNumber: update.rfNumber,
                                    newImageId: update.newImageId,
                                });
                            }
                        } catch (orphanErr) {
                            console.error("Failed to update some orphaned siblings:", orphanErr);
                        }
                    }
                } else {
                    await Axios.post("/Prescription/updateImage", {
                        storeID: this.prescription.storeID,
                        rxNumber: this.prescription.rxNumber,
                        rfNumber: this.prescription.rfNumber,
                        newImageId: payload.newImageId,
                    });

                    this.$notification(NotificationOptions.success("Hardcopy annotation saved."));
                }

                this.prescription.imageID = payload.newImageId;

                if (this.$refs.prescriptionImage) {
                    (this.$refs.prescriptionImage as any).fetchPrescriptionImageSource(`image/${payload.newImageId}`);
                }
            } catch (err: any) {
                console.error("Failed to update prescription with new image:", err);
                // Revert the imageID on failure
                this.prescription.imageID = payload.oldImageId ?? undefined;
                this.$notification(NotificationOptions.error(err?.response?.data || "Failed to save annotated image."));
            }
        }
    }

    onPdfAnnotationClosed() {
        // Reset quick annotate state when modal is closed
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;
    }

    backToPreviousPage(backToOrderPage = false) {
        const destination = decideVerificationBackDestination({
            backToOrderPage,
            routeOrderId: this.routeOrderId,
            loadedOrderId: this.order?.id,
            pageMode: this.pageMode,
            skipOrderVerificationAfterPV1: this.skipOrderVerificationAfterPV1,
            hasDispenseErrorRoute: !!this.routeDispenseErrorId,
            isBatch: !!this.$route.query.batch,
        });
        switch (destination.kind) {
            case 'orderVerification':
                this.$router.push({
                    name: 'OrderVerification',
                    params: {
                        orderId: destination.orderId.toString(),
                        routeMode: this.$route.params.routeMode ? 'pendingError' : '',
                    },
                    query: { preserveScroll: "1" },
                });
                break;
            case 'pendingDispenseErrors':
                this.$router.push({ name: 'PendingDispenseErrors' });
                break;
            case 'pv1Queue':
                this.$router.push({ name: 'PV1QueuePage' });
                break;
            case 'previous':
                this.$router.go(-1);
                break;
        }
    }

    postVerification() {
        if (!this.prescription) return;

        if (this.postingVerification) return;
        this.postingVerification = true;
        const PVNbr = this.pageMode == 'pv1' ? 1 : (this.pageMode == 'pv2' ? 2 : null);

        // Calculate verification timing (SD-3120)
        const totalSeconds = this.verificationLoadedAt > 0
            ? Math.round((performance.now() - this.verificationLoadedAt) / 1000)
            : null;

        const postData = {
            Prescription: this.prescription,
            Verification: {
                Patient: this.patientStatus,
                Prescriber: this.doctorStatus,
                Quantity: this.quantityStatus,
                Drug: this.drugStatus,
                Direction: this.directionStatus,
                dur: !this.drugUtilizationReviews?.items?.length ? null : this.durStatus,
            },
            DUR: !this.drugUtilizationReviews?.items?.length ? null : this.drugUtilizationReviews,
            PVNbr,
            VisibleDurationSeconds: this.verificationLoadedAt > 0 ? this.verificationVisibleSeconds : null,
            TotalDurationSeconds: totalSeconds,
        };

        const positiveIdHeader = this.verifiedToken ? { 'X-PositiveId-Token': this.verifiedToken } : undefined;
        return Axios.post(`/Prescription/${this.prescriptionIdentifier}/verification`, postData,
                          positiveIdHeader ? { headers: positiveIdHeader } : undefined)
                    .then(async response => {
                        // Token is single-use (bound to this correlation/target); clear regardless of branch below.
                        this.verifiedToken = '';
                        this.recordPageFocusTime(PageFocusEventClosedAction.Completed, totalSeconds);
                        const warning = response.data.warning;
                        if (warning) {
                            this.$notification(NotificationOptions.warning(`Verification saved with warning:\n\n${warning}`));
                            setTimeout(() => {
                                this.backToPreviousPage(true);
                            }, 3_000);
                        } else {
                            this.$notification(NotificationOptions.successSaveNotificationPreset("Verification"));

                            // Handle auto mode navigation
                            if (this.$store.autoModeEnabled) {
                                await this.handleAutoModeAdvance();
                            } else if (!this.hasBatchPrescriptions()) {
                                this.backToPreviousPage(true);
                            }
                        }
                    })
                    .catch(error => {
                        this.$notification(NotificationOptions.errorSaveNotificationPreset("Verification", error));
                        console.log('Error while posting verification', {
                            error,
                            response: error?.response,
                        });
                    })
                    .finally(() => {
                        this.postingVerification = false;
                        this.durStatus = false;
                    });
    }

    fetchDrugCategory(productID: number | string = 0) {
        if (!(this.drug?.drugId || productID)) return;
        if (this.drug?.source != DrugSource.GsddFederal) return;

        productID = productID as any || this.drug?.drugId as any;
        Axios.get<Array<DrugCategory>>(`Drug/${productID}/${this.drug.source}/categories`)
             .then(response => {
                 this.drugCategories = response.data.map(r => Object.assign(new DrugCategory(), r));
             })
             .catch(error => {
                 console.error('Error while getting drug categories', {
                     error,
                     response: error?.response,
                 });
             });
    }

    protected isReprintingLabel = false;

    async getPrinterByIPAddress() {
        try {
            this.isReprintingLabel = true;
            let endpoint = `Print/PrintByIPAddress/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}`;
            if (!this.activePrinter) {
                endpoint += `?ipAddress=${this.printerIpAddress}`;
            }
            const response = await Axios.post(endpoint);

            if (this.activePrinter) {
                // Collect all labels into an array
                const allLabels: string[] = [];

                if (response.data.warnings?.length) {
                    allLabels.push(...response.data.warnings);
                }

                if (response.data.primary) {
                    allLabels.push(response.data.primary);
                }

                if (response.data.otc) {
                    allLabels.push(response.data.otc);
                }

                const rawPrintRequest = {
                    printerName: this.activePrinter.name,
                    rawContents: allLabels,
                    documentName: "label-reprint",
                    copies: 1,
                };

                const workerPort = (this as any).$workerPort;
                if (workerPort) {
                    workerPort.postMessage(["clientServicePrintRaw", rawPrintRequest]);
                } else {
                    // Fallback to HTTP
                    const printerEndpoint = "http://localhost:7274/print/raw";
                    await Axios.post(printerEndpoint, rawPrintRequest);
                }
            }

            this.printerIpAddress = '';
            this.$notification(NotificationOptions.notificationOptionsPreset(`Label was successfully printed`, NotificationOptions.NotificationTypes.success));
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isReprintingLabel = false;
        }
    }

    get lastIpAddress(): any {
        const objStr = localStorage.getItem('printQueue_ipAddress');
        if (objStr) {
            return JSON.parse(objStr);
        } else {
            return null;
        }

    }

    @Watch('printerIpAddress')
    lastIpAddressChanged(val1: any, _val2: any) {
        if (val1 == new Printer()) {
            return;
        }
        localStorage.setItem('printQueue_ipAddress', JSON.stringify(val1));
        console.log('printerIpAddress', this.lastIpAddress);
    }

    get prescriptionIdentifier(): string {
        return this.prescription.rxID;
    }

    formattedLocalDate(value: Date): string {
        if (!value) return '';
        const date = moment(value);
        return date.format("L");
    }

    formattedDate(value: Date): string {
        if (!value) return '';
        const date = moment.utc(value);
        return date.format("L");
    }

    formattedDateAndTime(value: Date): string {
        return formatDateAndTime(value);
    }

    formattedPrecision(value: number, precision: number): string {
        if (value % 1 === 0) return value.toString();
        return value.toFixed(precision);
    }

    get expiration(): string {
        if (!this.prescription || !this.prescription.expirationDate) return "";
        return this.formattedDate(this.prescription.expirationDate);
    }

    get fillDate(): any {
        if (!this.prescription || !this.prescription.fillDate) return "";
        return this.formattedDate(this.prescription.fillDate);
    }

    get writtenDate(): any {
        if (!this.prescription || !this.prescription.writtenDate) return "";
        return this.formattedDate(this.prescription.writtenDate);
    }

    get drugExpDate(): any {
        if (!this.prescription || !this.prescription.drugExpirationDate) return "";
        return this.formatExpiryDate(this.prescription.drugExpirationDate);
    }

    formatExpiryDate(value: any) {
        return moment(value).format('YYYY-MM-DD');
    }


    get safety(): string {
        return this.patient && this.patient.childCaps ? "USE SAFETY CAP" : "USE NON-SAFETY CAP";
    }

    get safetyCapClasses(): string {
        return this.patient && this.patient.childCaps ? "" : "safety-cap-warning";
    }

    get safetyCapIcon(): string {
        return this.patient && this.patient.childCaps ? "" : "fas fa-exclamation-triangle";
    }

    protected drugImageURLs: string[] = [];
    private drugDetails: DrugProduct | null = null;
    private inventoryOnHand: number = 0;
    private inventoryAllocated: number = 0;

    @Watch('drugDetails')
    drugDetailsUpdate(value: DrugProduct | null, oldValue: DrugProduct | null) {
        if (value == oldValue) return;
        this.drugImageURLs = getDrugImageUrls(value?.drugId, value?.source);
    }

    get patientNameMatchClass() {
        try {
            if (!this.escriptData) return '';
            if (this.patient?.firstName.trim().toLowerCase() != this.escriptData.patient?.firstName.trim().toLowerCase()) return 'text-danger firstName';
            if (this.patient?.lastName.trim().toLowerCase() != this.escriptData.patient?.lastName.trim().toLowerCase()) return 'text-danger lastName';
            return 'text-success';
        } catch {
            return '';
        }
    }

    get patientDateOfBirthMatchClass() {
        try {
            if (!this.escriptData) return '';
            if (this.patient?.dateOfBirth != this.escriptData.patient?.dateOfBirth) return 'text-danger dob';
            return 'text-success';
        } catch {
            return '';
        }
    }

    get patientAllergyMatch(): string {
        if (this.escriptData == null) return '';
        if (this.patient.nkda && (this.escriptData.allergies?.length ?? 0) > 0) return 'text-danger';
        if (this.patient.nkda && (this.escriptData.allergies?.length ?? 0) == 0) return 'text-success';
        return 'text-warning';
    }

    get doctorNameMatch(): string {
        if (this.escriptData == null) return '';
        if (this.doctor?.firstName.trim().toLowerCase() != this.escriptData.prescriber?.firstName.trim().toLowerCase()) return 'text-danger';
        if (this.doctor?.lastName.trim().toLowerCase() != this.escriptData.prescriber?.lastName.trim().toLowerCase()) return 'text-danger';
        return 'text-success';
    }

    get doctorNpiMatch(): string {
        if (this.escriptData == null) return '';
        if (this.doctor?.npiNumber != this.escriptData.prescriber?.npiNumber) return 'text-danger';
        return 'text-success';
    }

    get doctorDeaMatch(): string {
        if (this.escriptData == null) return '';
        if (!this.escriptData.prescriber?.deaNumber) return '';
        if (this.doctor?.deaNumber != this.escriptData.prescriber?.deaNumber) return 'text-danger';
        return 'text-success';
    }

    get quantityMatch(): string {
        if (this.escriptData == null) return '';
        if (this.prescription.quantity != this.escriptData.prescribedMed?.quantityValue) return 'text-danger';
        return 'text-success';
    }

    get refillsMatch(): string {
        if (this.escriptData == null) return '';
        if (this.prescription.refills != this.escriptData.prescribedMed?.numberOfRefills) return 'text-danger';
        return 'text-success';
    }

    get dawMatch(): string {
        if (this.escriptData == null) return '';
        if (this.prescription.dawCode != this.escriptData.prescribedMed?.substitutions) return 'text-danger';
        // if (this.prescription.dawCode != 0
        //     && this.prescription.ndc11 != this.escriptData.prescribedMed.ndc11)
        // {
        //     return 'text-danger'
        // }
        return 'text-success';
    }

    get hasHardcopyPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Hardcopy Note");
    }

    get hasEditImagePermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Edit Image");
    }

    get hasCompoundReadPermission() {
        return this.$permissions.some(p => p.permissionCodeName === "Maintenance/Compound Drug/Read");
    }

    // Mirrors PriorAuthorizationListComponent.canCreatePA so the PV1 button honours the
    // same permissions as the Patient Dashboard's "Create PA" button (case-insensitive).
    get canCreatePA(): boolean {
        return this.$permissions.some(p =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization" ||
            p.permissionCodeName.toLowerCase() === "patient dashboard/create pa"
        );
    }

    newPriorAuthorization() {
        this.$bvModal.show("prior-authorization-create-modal");
    }

    get writtenMatch(): string {
        if (!this.escriptData?.prescribedMed?.writtenDate) return '';
        const writtenDateStartUtc = moment.utc(this.prescription.writtenDate).startOf('day');
        const escriptWrittenDateStartUtc = moment.utc(this.escriptData.prescribedMed?.writtenDate).startOf('day');
        if (!writtenDateStartUtc.isSame(escriptWrittenDateStartUtc)) return 'text-danger';
        return 'text-success';
    }

    protected dawDrugMismatch: boolean = false;

    get drugMatch(): string {
        this.dawDrugMismatch = false;
        if (!this.escriptData?.prescribedMed) {
            console.log("no escript data");
            return '';
        }

        if (this.drugDetails?.ndc == null) {
            console.log("no drugDetails");
            return '';
        }

        let processedNdc = this.drugDetails.ndc.replaceAll('-', '');
        if (processedNdc.length > 9) processedNdc = processedNdc.substring(0, 9);

        if (this.escriptData.prescribedMed.allowedNdcList?.length > 0
            && this.escriptData.prescribedMed.allowedNdcList.indexOf(processedNdc) >= 0) {
            if (this.prescription.dawCode == 0) {
                return 'text-success';
            } else {
                this.dawDrugMismatch = true;
            }
        }

        if (this.escriptData.prescribedMed.ndc11 == null) return '';
        let prescribedNdc = this.escriptData.prescribedMed.ndc11.replaceAll('-', '');
        if (prescribedNdc.length > 9) prescribedNdc = prescribedNdc.substring(0, 9);

        if (prescribedNdc != processedNdc) return 'text-danger';

        return 'text-success';
    }

    get fillDateOk(): string {
        if (moment(this.prescription.fillDate).isSameOrAfter(moment().subtract(2, 'weeks'))) {
            return 'text-success';
        }
        return 'text-warning';
    }

    get originOk(): string {
        if (this.escriptData == null && this.prescription.originCode == OriginCode.Escript) {
            return 'text-danger';
        }

        if (!this.escriptData?.store) {
            return '';
        }

        if (this.escriptData.store.npiNumber != this.store?.npiNumber && this.prescription.originCode != OriginCode.Transfer) {
            return 'text-danger';
        }

        if (this.escriptData.store.npiNumber == this.store?.npiNumber && this.prescription.originCode != OriginCode.Escript) {
            return 'text-danger';
        }

        return 'text-success';
    }

    @Watch('drug') drugUpdate(value: DrugProduct | null, oldValue: any) {
        if (!value || value && oldValue && oldValue.id) {
            this.drugDetails = value;
        }
        if (!value || !value.drugId || (oldValue && oldValue.packageID && +value.drugId == +oldValue.packageID)) return;
        if (this.prescription) this.prescription.packageID = +value.drugId; // ensure packageID is a number;

        if (value.source == DrugSource.GsddFederal) {
            Axios.get(`/Drug/${value.drugId}`)
                 .then(response => {
                     this.drugDetails = new Drug(value.drugId, response.data);
                 })
                 .catch(error => {
                     console.warn(error);
                 });
        }
        if (value.source == DrugSource.NonGsddProduct) {
            Axios.get(`/NonGSDD/${value.drugId}`)
                 .then(response => {
                     this.drugDetails = new NonGSDDDrug(value.drugId, response.data);
                 })
                 .catch(error => {
                     console.warn(error);
                 });
        }
        if (value.source == DrugSource.CompoundProduct) {
            Axios.get(`/Compound/${value.drugId}`)
                 .then(response => {
                     this.drugDetails = new CompoundDrug(value.drugId, response.data);
                 })
                 .catch(error => {
                     console.warn(error);
                 });
        }

    }

    async saveDispenseError() {
        this.savingDispenseError = true;
        this.dispenseError.pv = this.pageMode;
        try {
            const response = await Axios.post<DispenseError>("/DispenseError", this.dispenseError);
            if (response.data.id) {
                this.fetchDispenseErrors();
                this.$notification(NotificationOptions.notificationOptionsPreset(this.dispenseErrorLabel + " was reported.", NotificationOptions.NotificationTypes.info));

                this.$bvModal.hide("dispense-error-modal");  // Close modal first

                // Handle auto mode navigation after rejecting
                if (this.$store.autoModeEnabled) {
                    await this.handleAutoModeAdvance();
                } else if (this.hasBatchPrescriptions()) {
                    // If in batch mode, automatically move to next prescription WITHOUT verifying
                    await this.navigateToNextInBatch();
                } else {
                    this.backToPreviousPage();
                }
            }
        } catch (error) {
            console.error("Error saving error", {
                error,
                response: error?.response,
            });
            this.$notification(NotificationOptions.error(error));
            this.openErrorDispenseModal();
        } finally {
            this.savingDispenseError = false;
        }
    }

    buildAddressString(_patient: Patient) {
        return (this.patient.address1 + (this.patient.address2 ? ', ' + this.patient.address2 : '') + ', '
            + this.patient.addressCity + ', ' + this.patient.addressState + ' ' + this.patient.addressZip).toString();
    }

    buildAllergyString(allergies: GSDDPatientAllergy[]) {
        let result = "";
        allergies.forEach(allergy => result = result.concat(allergy.allergyName + ', '));
        result = result.slice(0, -2);
        return result;
    }

    async handleErrorStatusChange(action: string, errorId: number) {
        try {
            await this.fetchDispenseErrors();
        } catch (err) {
            console.warn("Failed to refresh dispense errors before navigation", err);
        }

        // Navigate after rejection in any batch-like mode
        if (action === 'reject') {
            if (this.$store.autoModeEnabled) {
                await this.handleAutoModeAdvance();
            } else if (this.hasBatchPrescriptions()) {
                await this.navigateToNextInBatch();
            }
        }
    }

    async fetchDispenseErrors(): Promise<void> {
        try {
            const response = await Axios.get<Array<DispenseError>>(`DispenseError/By-RxId/${this.prescription.rxID}/`);
            const findActiveDispenseErrorCallback = (err_1: DispenseError) => err_1.statusEnum != DispenseError.DispenseErrorStatusEnum.Accepted;
            const emptyDispenseError = new DispenseError(this.prescription);
            this.dispenseErrors = response.data.map((err_2: DispenseError) => Object.assign(new DispenseError(), err_2));
            if (this.dispenseErrors?.some(findActiveDispenseErrorCallback)) {
                this.dispenseError = this.dispenseErrors.find(findActiveDispenseErrorCallback) || emptyDispenseError;
            } else {
                this.dispenseError = emptyDispenseError;
            }
        } catch (err) {
            console.error("Error while refreshing errors", err);
            throw err;
        }
    }

    async verifyClicked() {
        if (!this.prescription) return;

        const hasLotNumber = !!this.prescription.lotNumber?.trim() || this.prescription.dispensingItems?.some(di => di.lotNumber?.trim());
        if (this.LotNumberRequired && !hasLotNumber) {
            return this.$bvModal.msgBoxOk(`Lot Number required.`);
        }

        const hasSerialNumber = !!this.prescription.serialNumber?.trim() || this.prescription.dispensingItems?.some(di => di.serialNumber?.trim());
        if (this.SerialNumberRequired && !hasSerialNumber) {
            return this.$bvModal.msgBoxOk(`Serial Number required.`);
        }

        if (this.PartnerFillRequired && (!this.prescription.partnerFillNumber || this.prescription.partnerFillNumber.trim().length == 0)) {
            return this.$bvModal.msgBoxOk(`Partner Fill Number required.`);
        }

        return this.postVerification();
    }

    openErrorDispenseModal() {
        this.$bvModal.show("dispense-error-modal");
    }

    cancelClicked() {
        this.backToPreviousPage();
    }

    openRxEdit() {
        this.$router.push({
            name: 'PrescriptionDetails',
            params: {
                storeID: this.prescription.storeID.toString(),
                rxNumber: this.prescription.rxNumber.toString(),
                rfNumber: (this.prescription.rfNumber as number).toString(),
            },
        });
    }

    reloadNotesForErrorAlert(dispenseErrorId: number) {
        const components = this.$refs.DispensingErrorAlertComponents as Array<DispensingErrorAlertComponent>;
        const component = components?.find((com: DispensingErrorAlertComponent) => com.value.id == dispenseErrorId);
        if (component) {
            component.loadNotes();
        } else {
            console.error("Reference dont exist for the Error Alert component");
        }
    }

    toggleUrgent() {
        const newUrgent = !this.prescription.isUrgent;
        const rxId = `${this.prescription.storeID}-${this.prescription.rxNumber}-${this.prescription.rfNumber}`;
        Axios.post(`/Prescription/${rxId}/urgent`, newUrgent, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
                this.prescription.isUrgent = newUrgent;
            })
            .catch(() => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed to update urgent status.", NotificationOptions.NotificationTypes.danger));
            });
    }

    cancelPV2() {
        this.$bvModal.msgBoxConfirm("By Cancelling PV2, any previous PV2 done for this RX will cancelled and inventory will be put back to stock. Are you sure?", {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        })
            .then(value => {
                if (!value) return;

                const rxId = this.prescription.rxID;
                if (!rxId) return;

                Axios.get(`/Prescription/${rxId}/cancel-pv2/`)
                     .then(response => {
                         if (response.data.cancelled) {
                             this.prescription.pV2ID = null;
                             this.$notification(NotificationOptions.notificationOptionsPreset("PV2 was cancelled.", NotificationOptions.NotificationTypes.success));
                         } else {
                             this.$notification(NotificationOptions.errorSaveNotificationPreset("PV2 cancellation"));
                         }
                     })
                     .catch(error => {
                         console.error("Error while cancelling PV2", {
                             error,
                             response: error?.response,
                         });
                         this.$notification(NotificationOptions.errorSaveNotificationPreset("PV2 cancellation", error));
                     });
            })
            .catch(err => {
                console.error("Error caught on cancel PV2 button.", err);
            });
    }

    onClaimsLoaded(billedClaims: Claim[]) {
        let allOffline = true;
        for (const claim of billedClaims) {
            if (!claim.isOffline) {
                allOffline = false;
                break;
            }
        }
        this.allClaimsAreOffline = allOffline;

        this.validatePrescriptionCopayAgainstClaims(billedClaims);
    }

    async validatePrescriptionCopayAgainstClaims(billedClaims: Claim[]) {
        this.verifiedPatientPay = null;

        if (!billedClaims?.length || !this.prescription) {
            return;
        }

        // This runs unprompted when claims load and PATCHes patient pay. For a remote worker on a
        // restricted-state patient that would be a silent write on a screen shown as view-only — and the
        // API now refuses it, so attempting it would only surface an error the user can't act on.
        if (this.disableForRemoteWorkerState) {
            return;
        }

        const matchingClaim = billedClaims.find(claim =>
            claim.storeId === this.prescription.storeID &&
            claim.rxNumber === this.prescription.rxNumber &&
            claim.rfNumber === this.prescription.rfNumber
        );

        if (!matchingClaim) {
            return;
        }

        const claimCopay = matchingClaim.overrideCopay != null ? matchingClaim.overrideCopay : (matchingClaim.patientPay || 0);
        const prescriptionPatientPay = this.prescription.patientPay || 0;

        if (prescriptionPatientPay !== claimCopay) {
            const originalPatientPay = this.prescription.patientPay;
            this.verifiedPatientPay = claimCopay;

            this.prescription.patientPay = claimCopay;

            try {
                await this.updatePrescriptionPatientPay(originalPatientPay, claimCopay);
                this.showCopayAdjustmentNotification(originalPatientPay, claimCopay);
            } catch (error) {
                console.log('Error updating patientpay mismatch with copay');
            }
        }
    }

    async updatePrescriptionPatientPay(oldAmount: number | null, newAmount: number) {
        try {
            const response = await Axios.patch(
                `/api/prescription/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}?patientPay=${newAmount}`
            );

            console.log(`Updated prescription.patientPay from $${oldAmount} to $${newAmount} based on claim copay`);

        } catch (error) {
            console.error('Failed to update prescription patientPay:', error);
            this.$notification(NotificationOptions.error('Failed to update patient pay amount automatically. Please try again.'));

            // Revert frontend change
            this.prescription.patientPay = oldAmount;
            this.verifiedPatientPay = null;
        }
    }

    showCopayAdjustmentNotification(oldAmount: number | null, newAmount: number) {
        const oldAmountStr = oldAmount != null ? `$${oldAmount.toFixed(2)}` : '$0.00';
        const newAmountStr = `$${newAmount.toFixed(2)}`;

        const message = `Patient Pay automatically adjusted from ${oldAmountStr} to ${newAmountStr} to match insurance claim copay`;

        this.$notification(NotificationOptions.success(message));
    }

    get showExplicitVerification() {
        if (this.disableFields) return false;
        if (this.pageMode == "pv1" && this.requireExplicitPV1) return true;
        return this.pageMode == "pv2" && this.requireExplicitPV2;

    }

    camelCaseToProperCase(str: string) {
        return str.replace(/([A-Z])([A-Z])([a-z])|([a-z])([A-Z])/g, function (_match, p1, p2, p3, p4, p5) {
            if (p1 && p2) {
                return p1 + p2.toLowerCase() + p3;
            } else {
                return p4 + ' ' + p5;
            }
        }).replace(/^./, function (str) {
            return str.toUpperCase();
        });
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "IP Address";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    protected isLoadingPrinters = true;

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;
                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;
            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-label-printer");

        if (lastPrinter) {
            this.activePrinter = this.localPrinters?.find(p => p.name === lastPrinter);
        }
    }

    @Watch('activePrinter')
    activePrinterChanged() {
        localStorage.setItem("active-label-printer", this.activePrinter?.name);
    }

    protected dispenseErrorLabel = "";

    async fetchConfiguration() {
        try {
            const resp = await this.$http.post<Record<string, any>>("/Configuration/values", [
                "DispenseErrorLabel", "EnableAcceptAndTransfer", "AllowAnnotationRevert",
                "ShowCrossPatientHardCopyScripts", "HideSamePatientHardCopyScripts",
                "EnablePositiveIdentification", "PositiveIdMethods", "EnableSupervisingPhysician",
                "SkipOrderVerificationAfterPV1",
            ]);
            this.dispenseErrorLabel = resp.data?.DispenseErrorLabel?.trim() ?? "Error";
            this.enableAcceptAndTransfer = resp.data?.EnableAcceptAndTransfer ?? false;
            this.skipOrderVerificationAfterPV1 = resp.data?.SkipOrderVerificationAfterPV1 === true;
            this.enableSupervisingPhysician = resp.data?.EnableSupervisingPhysician ?? false;
            this.allowAnnotationRevert = resp.data?.AllowAnnotationRevert ?? false;
            this.showCrossPatientHardCopyScripts = resp.data?.ShowCrossPatientHardCopyScripts ?? false;
            this.hideSamePatientHardCopyScripts = resp.data?.HideSamePatientHardCopyScripts ?? false;
            this.positiveIdEnabled = resp.data?.EnablePositiveIdentification ?? false;
            const methodsRaw = resp.data?.PositiveIdMethods;
            if (Array.isArray(methodsRaw)) {
                this.positiveIdMethods = methodsRaw;
            } else if (typeof methodsRaw === 'string' && methodsRaw) {
                try {
                    const parsed = JSON.parse(methodsRaw);
                    this.positiveIdMethods = Array.isArray(parsed) ? parsed : null;
                } catch {
                    // Malformed PositiveIdMethods JSON — fall back to "all methods allowed".
                    this.positiveIdMethods = null;
                }
            } else {
                this.positiveIdMethods = null;
            }
            this.positiveIdConfigLoaded = true;
            this.retryPendingSourceFetch();
        } catch {
            this.dispenseErrorLabel = "Error";
            this.enableAcceptAndTransfer = false;
            this.skipOrderVerificationAfterPV1 = false;
            // Best-effort: even on fetch failure mark the config as resolved (with safe defaults)
            // so the Save button isn't permanently disabled when positive-id is off.
            this.positiveIdConfigLoaded = true;
        }
    }

    get pageTitle() {
        return this.pageMode == 'pv2' ? 'Prescription Verification' : this.pageMode == 'audit' ? 'Audit' :
            'PV1';
    }

    openCreateTaskModal() {
        this.showTaskModal = true;
    }

    async onAcceptAndTransferComplete() {
        // After accept-and-transfer, navigate like we do after a successful verification
        if (this.hasBatchPrescriptions()) {
            await this.navigateToNextInBatch();
        } else if (this.$store.autoModeEnabled) {
            await this.handleAutoModeAdvance();
        } else {
            // Go back to the PV1 queue
            this.$router.push({ name: 'PV1QueuePage' });
        }
    }

    onTaskSaved() {
        this.showTaskModal = false;
    }
}

</script>

<style scoped>
::v-deep(.dropdown-item.active) {
    background-color: rgba(0, 0, 255, .05);
    font-weight: bold;
}

h4 {
    margin-top: 0;
}

p {
    margin-bottom: 0;
}

.bordered {
    border-style: solid;
    border-width: thin;
    border-color: currentColor;
}

.selected {
    border-style: solid;
    border-width: 2px;
    border-color: red;
}

.changes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
    gap: 10px;
}

.highlight {
    background: rgba(255, 255, 0, .2);
}

.image-wrapper {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch !important;
}

.script-image {
    height: 100vh;
    border: 1px solid black;
    flex-grow: 1;
    max-height: 100vh !important;
}

.actions {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 140px), 1fr));
    gap: 7px;
}

::v-deep(.source-col)
{
    width: 130px;
}

::v-deep(.source-datamatrix)
{
    background-color: #e8f5e9 !important; /* Light green for DataMatrix scans */
}

::v-deep(.source-upc)
{
    background-color: #e3f2fd !important; /* Light blue for UPC scans */
}

::v-deep(.source-parata)
{
    background-color: #e8f5e9 !important; /* Light green for Parata - same as DataMatrix (automated) */
}

::v-deep(.source-manual)
{
    background-color: #fff9c4 !important; /* Light yellow for Manual entries */
}

::v-deep(.source-external)
{
    /* Light orange for partner-reported lots. Deliberately unlike the scanned greens and the manual
       yellow: nobody here saw this container, and it was never checked against stock on hand. */
    background-color: #ffe0b2 !important;
}

/* Non-Safety Cap Warning Styles */
.safety-cap-warning {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 4px;
    background-color: #fff3cd;
    border: 2px solid #ff6b6b;
}

.safety-cap-warning i {
    color: #ff6b6b;
    font-size: 1.1rem;
}

.safety-cap-warning strong {
    color: #856404;
    font-weight: bold;
}

.dur-section-header {
    position: sticky;
    top: 0;
    z-index: 10;
    /* Frosted-glass background (.glass-pane) blurs the DUR list scrolling beneath the pinned header */
    /* Visual separation from the list scrolling underneath */
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 4px 0 0;
    margin-bottom: 16px;
}

.dur-section-header h5 {
    margin-bottom: 0;
}
</style>
